import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowUp, ArrowDown, Search, Plus, Download, Upload, Printer,
  Settings, Palette, SlidersHorizontal, Users, CheckCircle2, Clock3, X,
  Save, Edit2, Trash2, RefreshCw, ChevronLeft, UserPlus, ExternalLink,
  AlertTriangle, CalendarDays, Building2, MapPin, Star, Copy, Eye, EyeOff, Image, Phone, Repeat, ListPlus
} from 'lucide-react';
import config from '../config';
import SearchableSelect from './SearchableSelect';
import SmartRepeatQuickModal from './SmartRepeatQuickModal';
import WorkListPickerModal from './WorkListPickerModal';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { askDeleteCode, deleteHeaders } from '../deleteConfirm';

const API = `${config.PASSPORT_API_URL}/api/tests`;
const PASSPORT_API = `${config.PASSPORT_API_URL}/api/passports`;
const TRAVEL_DURATIONS = ['3 أشهر', '4 أشهر', '6 أشهر', 'عقد', 'تعقيب'];
const PASSPORT_STATUSES = ['جديد', 'قديم شركات'];
// نفس الاختيارات الموجودة حالياً في صفحة الجوازات.
const ABILITY_TESTS = ['لم يختبر', 'تحت الاختبار', 'لائق', 'غير لائق', 'اساسى', 'احتياطي', 'سائق ثاني'];
const CANDIDATE_PAGE_SIZE = 25;

const TEST_REPORT_COLOR_THEMES = {
  blue:      { label:'أزرق سماوي', dot:'#1976d2', bg:'linear-gradient(135deg,#f0f9ff,#e0f2fe)', headerBg:'linear-gradient(135deg,#1976d2,#42a5f5)', headerBorder:'#0d47a1', cardBg:'linear-gradient(135deg,#e3f2fd,#bbdefb)', cardBorder:'#1976d2', titleColor:'#0d47a1', rowOdd:'#ffffff', rowEven:'#e3f2fd', cellBorder:'#bbdefb', footerBg:'linear-gradient(135deg,#e3f2fd,#bbdefb)', footerBorder:'#1976d2' },
  navyBlue:  { label:'أزرق داكن', dot:'#1a237e', bg:'linear-gradient(135deg,#e8eaf6,#c5cae9)', headerBg:'linear-gradient(135deg,#1a237e,#3949ab)', headerBorder:'#0d1b6e', cardBg:'linear-gradient(135deg,#e8eaf6,#c5cae9)', cardBorder:'#1a237e', titleColor:'#0d1b6e', rowOdd:'#ffffff', rowEven:'#e8eaf6', cellBorder:'#c5cae9', footerBg:'linear-gradient(135deg,#e8eaf6,#c5cae9)', footerBorder:'#1a237e' },
  green:     { label:'أخضر زمردي', dot:'#2e7d32', bg:'linear-gradient(135deg,#f0fdf4,#dcfce7)', headerBg:'linear-gradient(135deg,#2e7d32,#66bb6a)', headerBorder:'#1b5e20', cardBg:'linear-gradient(135deg,#e8f5e9,#c8e6c9)', cardBorder:'#2e7d32', titleColor:'#1b5e20', rowOdd:'#ffffff', rowEven:'#e8f5e9', cellBorder:'#c8e6c9', footerBg:'linear-gradient(135deg,#e8f5e9,#c8e6c9)', footerBorder:'#2e7d32' },
  teal:      { label:'تيل فيروزي', dot:'#00695c', bg:'linear-gradient(135deg,#f0fdfa,#ccfbf1)', headerBg:'linear-gradient(135deg,#00695c,#26a69a)', headerBorder:'#004d40', cardBg:'linear-gradient(135deg,#e0f2f1,#b2dfdb)', cardBorder:'#00695c', titleColor:'#004d40', rowOdd:'#ffffff', rowEven:'#e0f2f1', cellBorder:'#b2dfdb', footerBg:'linear-gradient(135deg,#e0f2f1,#b2dfdb)', footerBorder:'#00695c' },
  purple:    { label:'بنفسجي', dot:'#6a1b9a', bg:'linear-gradient(135deg,#faf5ff,#ede9fe)', headerBg:'linear-gradient(135deg,#6a1b9a,#ab47bc)', headerBorder:'#4a148c', cardBg:'linear-gradient(135deg,#f3e5f5,#e1bee7)', cardBorder:'#6a1b9a', titleColor:'#4a148c', rowOdd:'#ffffff', rowEven:'#f3e5f5', cellBorder:'#e1bee7', footerBg:'linear-gradient(135deg,#f3e5f5,#e1bee7)', footerBorder:'#6a1b9a' },
  orange:    { label:'برتقالي دافئ', dot:'#e65100', bg:'linear-gradient(135deg,#fff7ed,#ffedd5)', headerBg:'linear-gradient(135deg,#e65100,#fb8c00)', headerBorder:'#bf360c', cardBg:'linear-gradient(135deg,#fff3e0,#ffe0b2)', cardBorder:'#e65100', titleColor:'#bf360c', rowOdd:'#ffffff', rowEven:'#fff3e0', cellBorder:'#ffe0b2', footerBg:'linear-gradient(135deg,#fff3e0,#ffe0b2)', footerBorder:'#e65100' },
  red:       { label:'أحمر رسمي', dot:'#c62828', bg:'linear-gradient(135deg,#fff5f5,#fee2e2)', headerBg:'linear-gradient(135deg,#c62828,#ef5350)', headerBorder:'#b71c1c', cardBg:'linear-gradient(135deg,#ffebee,#ffcdd2)', cardBorder:'#c62828', titleColor:'#b71c1c', rowOdd:'#ffffff', rowEven:'#ffebee', cellBorder:'#ffcdd2', footerBg:'linear-gradient(135deg,#ffebee,#ffcdd2)', footerBorder:'#c62828' },
  gray:      { label:'رمادي رسمي', dot:'#374151', bg:'linear-gradient(135deg,#f8fafc,#f1f5f9)', headerBg:'linear-gradient(135deg,#374151,#6b7280)', headerBorder:'#1f2937', cardBg:'linear-gradient(135deg,#f3f4f6,#e5e7eb)', cardBorder:'#374151', titleColor:'#1f2937', rowOdd:'#ffffff', rowEven:'#f3f4f6', cellBorder:'#e5e7eb', footerBg:'linear-gradient(135deg,#f3f4f6,#e5e7eb)', footerBorder:'#374151' }
};

const getUser = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
};
const getUserId = () => getUser()?.UserId || null;
const getPermissions = () => {
  try { return JSON.parse(getUser()?.Permissions || '{}') || {}; } catch { return {}; }
};
const readArray = (value) => Array.isArray(value) ? value : (Array.isArray(value?.data) ? value.data : []);
const toDateInput = (value) => value ? new Date(value).toISOString().slice(0, 10) : '';
const formatDate = (value) => value ? new Date(value).toLocaleDateString('ar-EG') : '-';
const withAlpha = (hex, alpha = '12') => /^#[0-9a-f]{6}$/i.test(hex || '') ? `${hex}${alpha}` : '#64748B12';
const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, ch => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[ch]));
const boolValue = (v) => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';


const TESTS_CONVERT_DEFAULTS_STORAGE_KEY = 'testsPassportConvertDefaultSettings';

const createSafeConvertDefaultSettings = (settings = {}) => {
  const base = {
    enabled: {
      CompanyID:false,
      TravelDuration:false,
      PassportStatus:false,
      AbilityTest:false,
      PassportProfessionID:false,
      PassportProfessionText:false,
      TravelProfessionID:false,
      DelegateID:false
    },
    values: {
      CompanyID:'',
      TravelDuration:'',
      PassportStatus:'',
      AbilityTest:'',
      PassportProfessionID:'',
      PassportProfessionText:'',
      TravelProfessionID:'',
      DelegateID:''
    }
  };
  const next = {
    ...base,
    ...(settings || {}),
    enabled:{...base.enabled,...(settings?.enabled || {})},
    values:{...base.values,...(settings?.values || {})}
  };
  return next;
};

const InlineSearchSelect = ({ options = [], value = '', onChange, placeholder = 'اختر...', emptyMessage = 'لا توجد نتائج', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find(option => String(option.value) === String(value));
  const normalizedQuery = String(query || '').trim().toLowerCase();
  const filtered = normalizedQuery
    ? options.filter(option => `${option.label || ''} ${option.subtitle || ''}`.toLowerCase().includes(normalizedQuery))
    : options;

  const choose = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="w-full" data-enter-save-skip="true">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(prev => !prev)}
        className={`w-full px-3 py-2.5 rounded-xl border text-right flex items-center justify-between gap-3 bg-white transition ${open ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-300 hover:border-slate-400'} disabled:bg-slate-100 disabled:cursor-not-allowed`}
      >
        <span className={selected ? 'font-bold text-slate-900' : 'text-slate-400'}>{selected?.label || placeholder}</span>
        <span className="text-slate-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && !disabled && (
        <div className="mt-2 rounded-2xl border border-slate-200 bg-white shadow-lg p-2">
          <div className="relative mb-2">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="اكتب للبحث..."
              className="w-full py-2.5 pr-9 pl-3 rounded-xl border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1 pr-1">
            {!!value && <button type="button" onClick={() => choose('')} className="w-full text-right px-3 py-2 rounded-xl text-slate-500 hover:bg-slate-50">مسح الاختيار</button>}
            {filtered.map(option => (
              <button
                type="button"
                key={String(option.value)}
                onClick={() => choose(String(option.value))}
                className={`w-full text-right px-3 py-2.5 rounded-xl border transition ${String(option.value)===String(value) ? 'bg-blue-50 border-blue-200 text-blue-800' : 'border-transparent hover:bg-slate-50 text-slate-800'}`}
              >
                <div className="font-bold">{option.label}</div>
                {option.subtitle && <div className="text-xs text-slate-400 mt-0.5">{option.subtitle}</div>}
              </button>
            ))}
            {!filtered.length && <div className="text-center text-sm text-slate-400 py-5">{emptyMessage}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const Modal = ({ open, onClose, title, children, width = 'max-w-3xl', footer }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" dir="rtl">
      <div className={`w-full ${width} max-h-[94vh] overflow-hidden rounded-3xl bg-white shadow-2xl flex flex-col`}>
        <div className="px-6 py-4 border-b bg-slate-50 flex items-center justify-between gap-4">
          <h2 className="font-black text-xl text-slate-900">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-200"><X size={20}/></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
        {footer && <div className="px-6 py-4 border-t bg-slate-50">{footer}</div>}
      </div>
    </div>
  );
};

const Field = ({ label, children, hint, required = false }) => (
  <div>
    <label className="block text-sm font-bold text-slate-700 mb-2">{label}{required && <span className="text-red-500 mr-1">*</span>}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const Button = ({ children, onClick, variant='primary', disabled=false, className='', type='button' }) => {
  const styles = {
    primary:'bg-blue-600 hover:bg-blue-700 text-white',
    success:'bg-emerald-600 hover:bg-emerald-700 text-white',
    danger:'bg-red-600 hover:bg-red-700 text-white',
    dark:'bg-slate-900 hover:bg-slate-800 text-white',
    light:'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200'
  };
  return <button type={type} disabled={disabled} onClick={onClick} className={`px-4 py-2.5 rounded-xl font-bold inline-flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}>{children}</button>;
};

const StatCard = ({ title, value, color='#2563EB', onClick, suffix='' }) => (
  <button onClick={onClick} className="text-right bg-white border rounded-2xl p-4 shadow-sm hover:shadow-md transition" style={{borderColor:withAlpha(color,'55')}}>
    <div className="text-xs font-bold text-slate-500">{title}</div>
    <div className="text-2xl font-black mt-1" style={{color}}>{value === null || value === undefined ? '-' : Number(value).toLocaleString('ar-EG')}{suffix}</div>
  </button>
);

const TestManagement = () => {
  const navigate = useNavigate();
  const permissions = getPermissions();
  const actionPerms = permissions['إدارة الاختبارات'];
  // صلاحيات تفصيلية مع توافق رجعي: إذا لم تكن الصلاحية الجديدة محفوظة بعد، نرجع للصلاحية القديمة المجمعة.
  const can = (name, fallbackNames = []) => {
    if (!actionPerms || Object.keys(actionPerms).length === 0) return true;
    if (Object.prototype.hasOwnProperty.call(actionPerms, name)) return actionPerms[name] === true;
    return fallbackNames.some(key => actionPerms[key] === true);
  };
  const dataPerms = permissions['البيانات'] || {};
  const canUseWorkLists = !Object.prototype.hasOwnProperty.call(dataPerms, 'قوائم العمل') || dataPerms['قوائم العمل'] === true;
  const workListActionPerms = permissions['إدارة قوائم العمل'];
  const canWorkListAction = (name) => !workListActionPerms || !Object.keys(workListActionPerms).length || !Object.prototype.hasOwnProperty.call(workListActionPerms,name) ? true : workListActionPerms[name] === true;
  const canAddToWorkList = canUseWorkLists && can('إضافة لقائمة عمل') && canWorkListAction('إضافة أشخاص');


  const candidateRequestRef = useRef(null);
  const sessionRequestRef = useRef(null);
  const globalSearchRequestRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [delegates, setDelegates] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [passportProfessionMode, setPassportProfessionMode] = useState('select');

  const [sessions, setSessions] = useState([]);
  const [sessionSearch, setSessionSearch] = useState('');
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [companyFilter, setCompanyFilter] = useState('');
  const [activeSession, setActiveSession] = useState(null);
  const [sessionFields, setSessionFields] = useState([]);
  const [stats, setStats] = useState({ Total:0, Linked:0, NotLinked:0, byStatus:[], AverageEvaluation:null, EvaluatedCount:0 });

  const [candidates, setCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateStatusFilter, setCandidateStatusFilter] = useState('');
  const [candidateLinkedFilter, setCandidateLinkedFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key:'', direction:'asc' });
  const [candidatePage, setCandidatePage] = useState(1);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatePagination, setCandidatePagination] = useState({
    page:1, pageSize:CANDIDATE_PAGE_SIZE, total:0, totalPages:1,
    from:0, to:0, hasPrevious:false, hasNext:false
  });

  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [cloneSourceSession, setCloneSourceSession] = useState(null);
  const [sessionForm, setSessionForm] = useState({ SessionTitle:'', CompanyID:'', TestType:'اختبار سواقة', TestDate:new Date().toISOString().slice(0,10), TestLocation:'', Notes:'' });

  const makeEmptyCandidate = () => ({ NameArabic:'', Age:'', LicenseType:'', PhoneNumber:'', PhoneNumber2:'', PhoneNumber3:'', DelegateID:'', TestResult:'', PassportStatus:'', PreviousCompanies:[], Notes:'', StatusID:'', CustomData:{} });
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [candidateForm, setCandidateForm] = useState(makeEmptyCandidate());
  const [saveAndNext, setSaveAndNext] = useState(true);
  const [candidatePhoneCount, setCandidatePhoneCount] = useState(1);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusForm, setStatusForm] = useState({ StatusID:null, StatusName:'', ColorHex:'#2563EB', DisplayOrder:0 });

  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [showFieldEditor, setShowFieldEditor] = useState(false);
  const [fieldForm, setFieldForm] = useState({ SessionFieldID:null, IsSystem:false, FieldKey:'', FieldName:'', FieldType:'text', OptionsText:'', IsRequired:false, IsEnabled:true, ShowInTable:true, MaxScore:10, InputMode:'default' });

  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const [showColumnsModal, setShowColumnsModal] = useState(false);
  const [columnAction, setColumnAction] = useState('export');
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [imageExportSettings, setImageExportSettings] = useState({
    showHeader:true,
    showTotal:true,
    showPageNum:true,
    showFooter:true,
    customTitle:'',
    maxRowsPerImage:25,
    colorTheme:'blue',
    printOrientation:'landscape'
  });
  const [delegateZipLoading, setDelegateZipLoading] = useState(false);
  const [delegateZipProgress, setDelegateZipProgress] = useState(null);

  const [candidateSaving, setCandidateSaving] = useState(false);

  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertIds, setConvertIds] = useState([]);
  const [convertForm, setConvertForm] = useState({ CompanyID:'', TravelDuration:'', AbilityTest:'', PassportProfessionID:'', PassportProfessionText:'', TravelProfessionID:'', PassportStatus:'', PreviousCompanies:[], DelegateID:'', AllowDuplicatePhone:false });
  const [converting, setConverting] = useState(false);
  const [duplicateRegisterQueue, setDuplicateRegisterQueue] = useState([]);
  const [markingRegistered, setMarkingRegistered] = useState(false);
  const [showConvertSettingsModal, setShowConvertSettingsModal] = useState(false);
  const [convertDefaultSettings, setConvertDefaultSettings] = useState(() => {
    try {
      return createSafeConvertDefaultSettings(JSON.parse(localStorage.getItem(TESTS_CONVERT_DEFAULTS_STORAGE_KEY) || '{}'));
    } catch {
      return createSafeConvertDefaultSettings();
    }
  });
  const [convertSettingsDraft, setConvertSettingsDraft] = useState(() => createSafeConvertDefaultSettings());

  const [showRegisteredCheck, setShowRegisteredCheck] = useState(false);
  const [checkingRegistered, setCheckingRegistered] = useState(false);
  const [registeredCheckResult, setRegisteredCheckResult] = useState(null);
  const [registeredCheckSearch, setRegisteredCheckSearch] = useState('');

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyCandidate, setHistoryCandidate] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);

  // نفس نافذة «التكرار الذكي السريع» المستخدمة داخل صفحة الجوازات.
  const [showSmartRepeatModal, setShowSmartRepeatModal] = useState(false);
  const [smartRepeatPassportData, setSmartRepeatPassportData] = useState([]);

  const [showWorkListPicker, setShowWorkListPicker] = useState(false);
  const [workListPickerItems, setWorkListPickerItems] = useState([]);

  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState([]);

  const alert = (text, type='success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };


  const confirmDelete123 = (description) => {
    const code = askDeleteCode(`${description}\n\nللتأكيد اكتب رمز تأكيد الحذف`);
    if (code === null) return false;
    if(!String(code).trim()) {
      alert('رمز التأكيد غير صحيح — لم يتم الحذف', 'error');
      return false;
    }
    return true;
  };
  const deleteOptions = { method:'DELETE', get headers(){ return deleteHeaders(); } };

  const api = async (url, options={}) => {
    const response = await fetch(url, options);
    const type = response.headers.get('content-type') || '';
    const data = type.includes('application/json') ? await response.json() : await response.blob();
    if (!response.ok) {
      const err = new Error(data?.error || 'حدث خطأ في العملية');
      err.status = response.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  const loadBase = async () => {
    if (!can('عرض الاختبارات')) return;
    try {
      setLoading(true);
      const [c,d,p,s,mode] = await Promise.all([
        api(`${config.PASSPORT_API_URL}/api/companies`),
        api(`${config.PASSPORT_API_URL}/api/delegates`),
        api(`${config.PASSPORT_API_URL}/api/professions`),
        api(`${API}/statuses`),
        api(`${PASSPORT_API}/settings/passport-profession-mode`).catch(() => ({ mode:'select' }))
      ]);
      setCompanies(readArray(c));
      setDelegates(readArray(d));
      setProfessions(readArray(p));
      setStatuses(readArray(s));
      setPassportProfessionMode(mode?.data?.mode === 'text' || mode?.mode === 'text' || mode?.value === 'text' ? 'text' : 'select');
    } catch (e) { alert(e.message, 'error'); }
    finally { setLoading(false); }
  };

  const loadSessions = async () => {
    if (!can('عرض الاختبارات')) return;
    const params = new URLSearchParams();
    if (yearFilter) params.set('year', yearFilter);
    if (companyFilter) params.set('companyId', companyFilter);
    if (sessionSearch) params.set('search', sessionSearch);
    sessionRequestRef.current?.abort?.();
    const controller = new AbortController();
    sessionRequestRef.current = controller;
    try { setSessions(readArray(await api(`${API}/sessions?${params.toString()}`, { signal: controller.signal }))); }
    catch (e) { if (e?.name !== 'AbortError') alert(e.message,'error'); }
    finally { if (sessionRequestRef.current === controller) sessionRequestRef.current = null; }
  };

  const loadFields = async () => {
    if (!activeSession?.SessionID) { setSessionFields([]); return; }
    try { setSessionFields(readArray(await api(`${API}/sessions/${activeSession.SessionID}/fields`))); }
    catch (e) { alert(e.message,'error'); }
  };

  const loadStats = async () => {
    if (!activeSession?.SessionID || !can('عرض إحصائيات الكشف',['عرض الاختبارات'])) return;
    try { setStats((await api(`${API}/sessions/${activeSession.SessionID}/stats`)) || {}); }
    catch (e) { alert(e.message,'error'); }
  };

  const loadCandidates = async (pageOverride = candidatePage) => {
    if (!activeSession?.SessionID || !can('عرض الاختبارات')) return;
    const params = new URLSearchParams();
    params.set('page', String(Math.max(1, Number(pageOverride) || 1)));
    if (candidateSearch) params.set('search',candidateSearch);
    if (candidateStatusFilter) params.set('statusId',candidateStatusFilter);
    if (candidateLinkedFilter) params.set('linked',candidateLinkedFilter);
    if (activeAdvancedFilterCount > 0) params.set('advanced', JSON.stringify(advancedFilters));
    if (sortConfig.key) {
      params.set('sortKey', sortConfig.key);
      params.set('sortDirection', sortConfig.direction || 'asc');
    }
    candidateRequestRef.current?.abort?.();
    const controller = new AbortController();
    candidateRequestRef.current = controller;
    try {
      setCandidatesLoading(true);
      const result = await api(`${API}/sessions/${activeSession.SessionID}/candidates?${params.toString()}`, { signal: controller.signal });
      setCandidates(readArray(result));
      const pg = result?.pagination || {};
      const normalized = {
        page:Number(pg.page || pageOverride || 1),
        pageSize:Number(pg.pageSize || CANDIDATE_PAGE_SIZE),
        total:Number(pg.total || 0),
        totalPages:Math.max(1,Number(pg.totalPages || 1)),
        from:Number(pg.from || 0),
        to:Number(pg.to || 0),
        hasPrevious:!!pg.hasPrevious,
        hasNext:!!pg.hasNext
      };
      setCandidatePagination(normalized);
      if (normalized.page !== candidatePage) setCandidatePage(normalized.page);
      setSelectedIds(new Set());
    } catch (e) { if (e?.name !== 'AbortError') alert(e.message,'error'); }
    finally {
      if (candidateRequestRef.current === controller) {
        candidateRequestRef.current = null;
        setCandidatesLoading(false);
      }
    }
  };

  const refreshCandidatesAndStats = async () => {
    await Promise.all([loadCandidates(candidatePage), loadStats()]);
  };

  const checkRegisteredByPhone = async () => {
    if (!activeSession?.SessionID) return;
    try {
      setShowRegisteredCheck(true);
      setCheckingRegistered(true);
      setRegisteredCheckSearch('');
      const result = await api(`${API}/sessions/${activeSession.SessionID}/registered-by-phone`);
      setRegisteredCheckResult(result || null);
      const matched = Number(result?.registeredCandidates || 0);
      alert(matched ? `تم الفحص — يوجد ${matched} شخص مسجل سابقًا بنفس رقم الهاتف` : 'تم الفحص — لم يتم العثور على أرقام مسجلة سابقًا');
    } catch (e) {
      setRegisteredCheckResult(null);
      alert(e.message, 'error');
    } finally {
      setCheckingRegistered(false);
    }
  };

  useEffect(() => { loadBase(); }, []);
  useEffect(() => { if (!activeSession) loadSessions(); }, [yearFilter, companyFilter]);
  useEffect(() => {
    if (!activeSession) {
      const t = setTimeout(loadSessions, 300);
      return () => clearTimeout(t);
    }
  }, [sessionSearch]);
  useEffect(() => {
    if (!activeSession) return;
    setCandidatePage(1);
    setAdvancedFilters({});
    setSortConfig({ key:'', direction:'asc' });
    setSelectedIds(new Set());
    loadFields();
    loadStats();
  }, [activeSession?.SessionID]);

  // تحميل 25 سجل فقط من السيرفر. البحث والفلاتر والترتيب كلها Server-side مع debounce بسيط.
  useEffect(() => {
    if (!activeSession) return;
    const t = setTimeout(() => loadCandidates(candidatePage), 260);
    return () => clearTimeout(t);
  }, [activeSession?.SessionID, candidatePage, candidateSearch, candidateStatusFilter, candidateLinkedFilter, advancedFilters, sortConfig]);
  useEffect(() => {
    setShowRegisteredCheck(false);
    setRegisteredCheckResult(null);
    setRegisteredCheckSearch('');
  }, [activeSession?.SessionID]);
  useEffect(() => {
    if (globalSearch.trim().length < 2) { setGlobalResults([]); return; }
    const t = setTimeout(async () => {
      globalSearchRequestRef.current?.abort?.();
      const controller = new AbortController();
      globalSearchRequestRef.current = controller;
      try { setGlobalResults(readArray(await api(`${API}/search?q=${encodeURIComponent(globalSearch.trim())}`, { signal: controller.signal }))); }
      catch (e) { if (e?.name !== 'AbortError') setGlobalResults([]); }
      finally { if (globalSearchRequestRef.current === controller) globalSearchRequestRef.current = null; }
    }, 350);
    return () => clearTimeout(t);
  }, [globalSearch]);
  useEffect(() => () => {
    candidateRequestRef.current?.abort?.();
    sessionRequestRef.current?.abort?.();
    globalSearchRequestRef.current?.abort?.();
  }, []);

  const companyOptions = companies.map(c => ({ value:String(c.CompanyID), label:c.CompanyName }));
  const normalizeCandidatePreviousCompanies = (value) => {
    const items = Array.isArray(value) ? value : (String(value||'').trim() ? String(value).split(/[,،;\n|]+/) : []);
    return [...new Set(items.map(item=>{
      const token=String(item||'').trim();
      if(!token) return '';
      const byId=companies.find(c=>String(c.CompanyID)===token);
      if(byId) return String(byId.CompanyID);
      const byName=companies.find(c=>String(c.CompanyName||'').trim()===token);
      return byName?String(byName.CompanyID):token;
    }).filter(Boolean))];
  };
  const delegateOptions = delegates.map(d => ({ value:String(d.DelegateID), label:d.DelegateName }));
  const professionOptions = professions.map(p => ({ value:String(p.ProfessionID), label:p.ProfessionName }));
  const candidatePhoneNumbers = (candidate) => [...new Set([
    String(candidate?.PhoneNumber||'').trim(),
    String(candidate?.PhoneNumber2||'').trim(),
    String(candidate?.PhoneNumber3||'').trim()
  ].filter(Boolean))];
  const candidatePhonesText = (candidate, separator=' / ') => candidatePhoneNumbers(candidate).join(separator);
  const sortedFields = useMemo(() => [...sessionFields].sort((a,b)=>(a.DisplayOrder||0)-(b.DisplayOrder||0) || a.SessionFieldID-b.SessionFieldID), [sessionFields]);
  const enabledFields = useMemo(() => sortedFields.filter(f=>boolValue(f.IsEnabled)), [sortedFields]);
  const tableFields = useMemo(() => enabledFields.filter(f=>boolValue(f.ShowInTable)), [enabledFields]);
  const scoreFields = useMemo(() => enabledFields.filter(f=>f.FieldType==='score'), [enabledFields]);
  const birthYearField = useMemo(() => enabledFields.find(f=>f.FieldType==='birth_year') || null, [enabledFields]);
  const visibleRegisteredMatches = useMemo(() => {
    const rows = readArray(registeredCheckResult?.matches);
    const q = String(registeredCheckSearch || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(item => {
      const passportText = (item.passports || []).map(p=>`${p.NameArabic||''} ${p.OfficeSerial||''} ${p.PassportNumber||''} ${p.CompanyName||''}`).join(' ');
      return `${item.NameArabic||''} ${(item.PhoneNumbers||[item.PhoneNumber]).join(' ')} ${passportText}`.toLowerCase().includes(q);
    });
  }, [registeredCheckResult, registeredCheckSearch]);
  const calculateAgeFromBirthYear = (value) => {
    const year = Number(value);
    const currentYear = new Date().getFullYear();
    if (!Number.isInteger(year) || year < 1900 || year > currentYear) return '';
    return currentYear - year;
  };

  const openNewSession = () => {
    setEditingSession(null);
    setCloneSourceSession(null);
    setSessionForm({ SessionTitle:'', CompanyID:'', TestType:'اختبار سواقة', TestDate:new Date().toISOString().slice(0,10), TestLocation:'', Notes:'' });
    setShowSessionModal(true);
  };

  const openCloneSession = (s) => {
    if (!s?.SessionID) return;
    setEditingSession(null);
    setCloneSourceSession(s);
    setSessionForm({
      SessionTitle:`${s.SessionTitle || 'كشف الاختبارات'} - نسخة فارغة`,
      CompanyID:String(s.CompanyID||''),
      TestType:s.TestType||'اختبار سواقة',
      TestDate:new Date().toISOString().slice(0,10),
      TestLocation:s.TestLocation||'',
      Notes:''
    });
    setShowSessionModal(true);
  };

  const saveSession = async () => {
    try {
      if (!sessionForm.CompanyID) throw new Error('اختر الشركة');
      if (!sessionForm.TestDate) throw new Error('حدد تاريخ الاختبار');
      const isClone = !!cloneSourceSession?.SessionID;
      const url = editingSession
        ? `${API}/sessions/${editingSession.SessionID}`
        : isClone
          ? `${API}/sessions/${cloneSourceSession.SessionID}/clone-empty`
          : `${API}/sessions`;
      const method = editingSession ? 'PUT' : 'POST';
      const result = await api(url, { method, headers:{'Content-Type':'application/json'}, body:JSON.stringify({ ...sessionForm, CreatedByUserID:getUserId() }) });
      setShowSessionModal(false);
      await loadSessions();
      alert(editingSession ? 'تم تعديل الكشف' : isClone ? 'تم إنشاء نسخة فارغة بنفس الحقول والترتيب' : 'تم إنشاء كشف الاختبار');
      if (!editingSession && result?.data?.SessionID) {
        setActiveSession({ ...result.data, CompanyName: companies.find(c=>String(c.CompanyID)===String(result.data.CompanyID))?.CompanyName || '' });
      }
      setCloneSourceSession(null);
    } catch (e) { alert(e.message,'error'); }
  };

  const editSession = (s) => {
    setCloneSourceSession(null);
    setEditingSession(s);
    setSessionForm({ SessionTitle:s.SessionTitle||'', CompanyID:String(s.CompanyID||''), TestType:s.TestType||'', TestDate:toDateInput(s.TestDate), TestLocation:s.TestLocation||'', Notes:s.Notes||'' });
    setShowSessionModal(true);
  };

  const deleteSession = async (s) => {
    if (!confirmDelete123(`سيتم حذف كشف «${s.SessionTitle}» من العرض.`)) return;
    try { await api(`${API}/sessions/${s.SessionID}`,deleteOptions); await loadSessions(); alert('تم حذف الكشف'); }
    catch(e){alert(e.message,'error');}
  };

  const openSession = (s) => {
    setActiveSession(s);
    setCandidateSearch('');
    setCandidateStatusFilter('');
    setCandidateLinkedFilter('');
    setAdvancedFilters({});
    setShowAdvancedFilters(false);
    setSortConfig({ key:'', direction:'asc' });
  };

  const candidateCustomValue = (candidate, field) => {
    const data = candidate?.CustomData || {};
    if (Object.prototype.hasOwnProperty.call(data, field.FieldKey)) return data[field.FieldKey];
    const legacy = String(field.FieldKey || '').match(/^custom:(\d+)$/)?.[1];
    return legacy && Object.prototype.hasOwnProperty.call(data, legacy) ? data[legacy] : '';
  };

  // قيمة موحدة لأي حقل، لاستخدامها في الفرز والفلاتر والتصدير.
  const candidateFieldValue = (candidate, field) => {
    if (!field) return '';
    if (boolValue(field.IsSystem)) {
      if (field.FieldKey === 'DelegateID') return candidate.DelegateID ?? '';
      if (field.FieldKey === 'StatusID') return candidate.StatusID ?? '';
      if (field.FieldKey === 'PreviousCompanies') return normalizeCandidatePreviousCompanies(candidate.PreviousCompanies);
      if (field.FieldKey === 'PhoneNumber') return candidatePhonesText(candidate);
      return candidate[field.FieldKey] ?? '';
    }
    return candidateCustomValue(candidate, field);
  };

  const filterKindForField = (field) => {
    if (!field) return 'text';
    if (field.FieldKey === 'TestResult') return 'select_multi';
    if (field.FieldType === 'multiselect') return 'multiselect';
    if (['DelegateID','StatusID','PassportStatus','LicenseType','PreviousCompanies'].includes(field.FieldKey)) return 'select';
    if (field.FieldType === 'select') return 'select';
    if (field.FieldType === 'checkbox') return 'checkbox';
    if (field.FieldType === 'date') return 'date';
    if (['number','score','birth_year'].includes(field.FieldType) || field.FieldKey === 'Age') return 'range';
    return 'text';
  };

  const filterOptionsForField = (field) => {
    if (!field) return [];
    if (field.FieldKey === 'DelegateID') return delegateOptions;
    if (field.FieldKey === 'StatusID') return statuses.map(s=>({ value:String(s.StatusID), label:s.StatusName }));
    if (field.FieldKey === 'PassportStatus') return PASSPORT_STATUSES.map(v=>({ value:v, label:v }));
    if (field.FieldKey === 'PreviousCompanies') return companyOptions;
    if (field.FieldKey === 'TestResult') return (field.Options||[]).map(v=>({ value:String(v), label:String(v) }));
    return (field.Options||[]).map(v=>({ value:String(v), label:String(v) }));
  };

  const updateAdvancedFilter = (fieldKey, value) => {
    setCandidatePage(1);
    setAdvancedFilters(prev => ({ ...prev, [fieldKey]: value }));
  };

  const clearAdvancedFilters = () => {
    setCandidatePage(1);
    setAdvancedFilters({});
  };

  const activeAdvancedFilterCount = useMemo(() => Object.values(advancedFilters).filter(value => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.some(v => String(v ?? '').trim() !== '');
    if (typeof value === 'object') return Object.values(value).some(v => v !== null && v !== undefined && String(v).trim() !== '');
    return String(value).trim() !== '';
  }).length, [advancedFilters]);

  const candidateMatchesAdvancedFilters = (candidate) => enabledFields.every(field => {
    const filter = advancedFilters[field.FieldKey];
    if (filter === null || filter === undefined || filter === '') return true;
    const kind = filterKindForField(field);
    const raw = candidateFieldValue(candidate, field);

    if (kind === 'text') {
      const q=String(filter||'').trim().toLocaleLowerCase('ar');
      if (!q) return true;
      return String(raw??'').toLocaleLowerCase('ar').includes(q);
    }
    if (kind === 'select_multi') {
      const wanted=(Array.isArray(filter)?filter:[filter]).map(String).filter(Boolean);
      if(!wanted.length) return true;
      return wanted.includes(String(raw??''));
    }
    if (kind === 'multiselect') {
      const wanted=(Array.isArray(filter)?filter:[filter]).map(String).filter(Boolean);
      if(!wanted.length) return true;
      const selected=Array.isArray(raw)?raw.map(String):[];
      return wanted.some(v=>selected.includes(v));
    }
    if (kind === 'select') {
      const wanted=String(filter||'').trim();
      if (!wanted) return true;
      if (Array.isArray(raw)) return raw.map(String).includes(wanted);
      return String(raw??'') === wanted;
    }
    if (kind === 'checkbox') {
      if (filter === '') return true;
      const checked=boolValue(raw);
      return String(filter)==='1' ? checked : !checked;
    }
    if (kind === 'range') {
      const min=filter?.min; const max=filter?.max;
      if ((min===''||min===undefined) && (max===''||max===undefined)) return true;
      const num=Number(raw);
      if (!Number.isFinite(num)) return false;
      if (min!=='' && min!==undefined && Number.isFinite(Number(min)) && num<Number(min)) return false;
      if (max!=='' && max!==undefined && Number.isFinite(Number(max)) && num>Number(max)) return false;
      return true;
    }
    if (kind === 'date') {
      const from=filter?.from||''; const to=filter?.to||'';
      if (!from && !to) return true;
      if (!raw) return false;
      const valueDate=new Date(raw);
      if (Number.isNaN(valueDate.getTime())) return false;
      if (from) { const d=new Date(`${from}T00:00:00`); if (valueDate<d) return false; }
      if (to) { const d=new Date(`${to}T23:59:59`); if (valueDate>d) return false; }
      return true;
    }
    return true;
  });

  const sortValueForCandidate = (candidate, key) => {
    const field=enabledFields.find(f=>f.FieldKey===key);
    if (!field) return '';
    if (key==='DelegateID') return candidate.DelegateName||'';
    if (key==='StatusID') return candidate.StatusName||'';
    if (key==='PreviousCompanies') return normalizeCandidatePreviousCompanies(candidate.PreviousCompanies).map(id=>companies.find(c=>String(c.CompanyID)===String(id))?.CompanyName||id).join('، ');
    return candidateFieldValue(candidate,field);
  };

  // candidates تحتوي الصفحة الحالية فقط؛ الفلاتر والترتيب تم تطبيقهما داخل SQL قبل OFFSET/FETCH.
  const displayedCandidates = useMemo(() => candidates, [candidates]);

  const toggleSort = (fieldKey) => {
    setCandidatePage(1);
    setSortConfig(prev => prev.key===fieldKey
      ? { key:fieldKey, direction:prev.direction==='asc'?'desc':'asc' }
      : { key:fieldKey, direction:'asc' });
  };

  const paginationItems = useMemo(() => {
    const total = Math.max(1, Number(candidatePagination.totalPages || 1));
    const current = Math.min(total, Math.max(1, Number(candidatePagination.page || 1)));
    if (total <= 7) return Array.from({length:total},(_,i)=>i+1);
    const pages = new Set([1,total,current-1,current,current+1]);
    if (current <= 4) [2,3,4,5].forEach(p=>pages.add(p));
    if (current >= total-3) [total-4,total-3,total-2,total-1].forEach(p=>pages.add(p));
    const sorted=[...pages].filter(p=>p>=1&&p<=total).sort((a,b)=>a-b);
    const out=[];
    sorted.forEach((p,i)=>{
      if(i>0 && p-sorted[i-1]>1) out.push(`ellipsis-${p}`);
      out.push(p);
    });
    return out;
  }, [candidatePagination.page, candidatePagination.totalPages]);

  const goToCandidatePage = (page) => {
    const total=Math.max(1,Number(candidatePagination.totalPages||1));
    const next=Math.min(total,Math.max(1,Number(page)||1));
    if(next===candidatePage) return;
    setCandidatePage(next);
    window.requestAnimationFrame(()=>document.getElementById('tests-candidates-table')?.scrollIntoView({behavior:'smooth',block:'start'}));
  };

  const visibleSelectedIds = useMemo(() => new Set(displayedCandidates.filter(c=>selectedIds.has(c.CandidateID)).map(c=>c.CandidateID)), [displayedCandidates, selectedIds]);

  const openCandidate = (candidate=null) => {
    if (candidate) {
      setEditingCandidate(candidate);
      setCandidateForm({
        NameArabic:candidate.NameArabic||'',
        Age:candidate.Age??'',
        LicenseType:candidate.LicenseType||'',
        PhoneNumber:candidate.PhoneNumber||'',
        PhoneNumber2:candidate.PhoneNumber2||'',
        PhoneNumber3:candidate.PhoneNumber3||'',
        DelegateID:candidate.DelegateID ? String(candidate.DelegateID) : '',
        TestResult:candidate.TestResult||'',
        PassportStatus:candidate.PassportStatus||'',
        PreviousCompanies:normalizeCandidatePreviousCompanies(candidate.PreviousCompanies),
        Notes:candidate.Notes||'',
        StatusID:candidate.StatusID ? String(candidate.StatusID) : '',
        CustomData:{ ...(candidate.CustomData||{}) }
      });
      setSaveAndNext(false);
      setCandidatePhoneCount(candidate.PhoneNumber3 ? 3 : (candidate.PhoneNumber2 ? 2 : 1));
    } else {
      setEditingCandidate(null);
      setCandidateForm(makeEmptyCandidate());
      setSaveAndNext(true);
      setCandidatePhoneCount(1);
    }
    setShowCandidateModal(true);
  };

  const saveCandidate = async () => {
    if (candidateSaving) return;
    try {
      setCandidateSaving(true);
      if (!String(candidateForm.NameArabic||'').trim()) throw new Error('الاسم مطلوب');
      const derivedAge = birthYearField ? calculateAgeFromBirthYear(candidateCustomValue(candidateForm,birthYearField)) : candidateForm.Age;
      const payload = { ...candidateForm, Age:derivedAge, CreatedByUserID:getUserId(), UpdatedByUserID:getUserId() };
      if (editingCandidate) await api(`${API}/candidates/${editingCandidate.CandidateID}`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      else await api(`${API}/sessions/${activeSession.SessionID}/candidates`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      await refreshCandidatesAndStats();
      if (!editingCandidate && saveAndNext) {
        setCandidateForm(makeEmptyCandidate());
        setCandidatePhoneCount(1);
        alert('تم الحفظ — جاهز للشخص التالي');
      } else {
        setShowCandidateModal(false);
        alert(editingCandidate?'تم تعديل البيانات':'تم إضافة الشخص');
      }
    } catch(e){alert(e.message,'error');}
    finally { setCandidateSaving(false); }
  };

  const handleCandidateFormKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.ctrlKey || event.altKey || event.metaKey || event.isComposing) return;
    const target = event.target;
    const tag = String(target?.tagName || '').toUpperCase();
    const type = String(target?.type || '').toLowerCase();
    if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'SELECT') return;
    if (['checkbox','radio','file','color','date'].includes(type)) return;
    if (target?.closest?.('[data-enter-save-skip="true"]')) return;
    event.preventDefault();
    if (!candidateSaving) saveCandidate();
  };

  const deleteCandidate = async (c) => {
    if (!confirmDelete123(`سيتم حذف ${c.NameArabic} من هذا الكشف.`)) return;
    try { await api(`${API}/candidates/${c.CandidateID}`,deleteOptions); await refreshCandidatesAndStats(); alert('تم حذف الشخص'); }
    catch(e){alert(e.message,'error');}
  };

  const quickStatus = async (candidateId, statusId) => {
    try {
      await api(`${API}/candidates/${candidateId}/status`,{method:'PUT',headers:{'Content-Type':'application/json'},body:JSON.stringify({StatusID:statusId||null,UpdatedByUserID:getUserId()})});
      await refreshCandidatesAndStats();
    } catch(e){alert(e.message,'error');}
  };

  const saveStatus = async () => {
    try {
      if (!statusForm.StatusName.trim()) throw new Error('اكتب اسم الحالة');
      const url=statusForm.StatusID?`${API}/statuses/${statusForm.StatusID}`:`${API}/statuses`;
      await api(url,{method:statusForm.StatusID?'PUT':'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(statusForm)});
      setStatuses(readArray(await api(`${API}/statuses`)));
      setStatusForm({StatusID:null,StatusName:'',ColorHex:'#2563EB',DisplayOrder:0});
      alert('تم حفظ الحالة');
      if(activeSession) refreshCandidatesAndStats();
    } catch(e){alert(e.message,'error');}
  };

  const deleteStatus = async (id) => {
    if(!confirmDelete123('سيتم إيقاف هذه الحالة. السجلات القديمة ستظل محتفظة بها.'))return;
    try{await api(`${API}/statuses/${id}`,deleteOptions);setStatuses(readArray(await api(`${API}/statuses`)));alert('تم إيقاف الحالة');}
    catch(e){alert(e.message,'error');}
  };

  const openAddField = () => {
    setFieldForm({ SessionFieldID:null, IsSystem:false, FieldKey:'', FieldName:'', FieldType:'text', OptionsText:'', IsRequired:false, IsEnabled:true, ShowInTable:true, MaxScore:10, InputMode:'default' });
    setShowFieldEditor(true);
  };

  const openEditField = (f) => {
    setFieldForm({
      SessionFieldID:f.SessionFieldID,
      IsSystem:boolValue(f.IsSystem),
      FieldKey:f.FieldKey,
      FieldName:f.FieldName,
      FieldType:f.FieldType,
      OptionsText:(f.Options||[]).join('\n'),
      IsRequired:boolValue(f.IsRequired),
      IsEnabled:boolValue(f.IsEnabled),
      ShowInTable:boolValue(f.ShowInTable),
      MaxScore:f.MaxScore || 10,
      DisplayOrder:f.DisplayOrder || 0,
      InputMode:f.InputMode || 'default'
    });
    setShowFieldEditor(true);
  };

  const saveField = async () => {
    try {
      const normalizedFieldName = fieldForm.FieldType==='birth_year' ? 'سنة الميلاد' : String(fieldForm.FieldName||'').trim();
      if(!normalizedFieldName) throw new Error('اكتب اسم الحقل');
      const payload={
        ...fieldForm,
        FieldName:normalizedFieldName,
        Options:fieldForm.OptionsText.split(/[,،\n]/).map(v=>v.trim()).filter(Boolean),
        MaxScore:fieldForm.FieldType==='score' ? Number(fieldForm.MaxScore||10) : null
      };
      const url=fieldForm.SessionFieldID?`${API}/sessions/${activeSession.SessionID}/fields/${fieldForm.SessionFieldID}`:`${API}/sessions/${activeSession.SessionID}/fields`;
      await api(url,{method:fieldForm.SessionFieldID?'PUT':'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      await loadFields();
      setShowFieldEditor(false);
      alert('تم حفظ إعداد الحقل لهذا الكشف فقط');
    }catch(e){alert(e.message,'error');}
  };

  const quickUpdateField = async (field, patch) => {
    try {
      await api(`${API}/sessions/${activeSession.SessionID}/fields/${field.SessionFieldID}`,{
        method:'PUT', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({
          FieldName:field.FieldName,
          FieldType:field.FieldType,
          Options:field.Options||[],
          DisplayOrder:field.DisplayOrder,
          IsRequired:boolValue(field.IsRequired),
          IsEnabled:boolValue(field.IsEnabled),
          ShowInTable:boolValue(field.ShowInTable),
          MaxScore:field.MaxScore,
          InputMode:field.InputMode || 'default',
          ...patch
        })
      });
      await loadFields();
    } catch(e){alert(e.message,'error');}
  };

  const moveField = async (fieldId, delta) => {
    const list=[...sortedFields];
    const idx=list.findIndex(f=>f.SessionFieldID===fieldId);
    const target=idx+delta;
    if(idx<0||target<0||target>=list.length)return;
    [list[idx],list[target]]=[list[target],list[idx]];
    try{
      await api(`${API}/sessions/${activeSession.SessionID}/fields/reorder`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({fieldIds:list.map(f=>f.SessionFieldID)})});
      await loadFields();
    }catch(e){alert(e.message,'error');}
  };

  const deleteField = async (field) => {
    const action = boolValue(field.IsSystem) ? 'إخفاء هذا الحقل من الكشف؟ يمكنك إظهاره لاحقاً من التعديل.' : 'حذف هذا الحقل من هذا الكشف؟ البيانات القديمة ستظل محفوظة.';
    if(!confirmDelete123(action))return;
    try{await api(`${API}/sessions/${activeSession.SessionID}/fields/${field.SessionFieldID}`,deleteOptions);await loadFields();alert(boolValue(field.IsSystem)?'تم إخفاء الحقل':'تم حذف الحقل');}
    catch(e){alert(e.message,'error');}
  };

  const downloadTemplate = async () => {
    try {
      const blob=await api(`${API}/template?sessionId=${activeSession.SessionID}`);
      const a=document.createElement('a');
      a.href=URL.createObjectURL(blob);
      a.download=`نموذج_${activeSession.SessionTitle||'الاختبارات'}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch(e){alert(e.message,'error');}
  };

  const previewImport = async () => {
    if(!importFile){alert('اختر ملف XLSX أو CSV','error');return;}
    try{
      setImporting(true);
      const fd=new FormData();
      fd.append('file',importFile);
      fd.append('sessionId',activeSession.SessionID);
      setImportPreview(await api(`${API}/import-preview`,{method:'POST',body:fd}));
    }catch(e){alert(e.message,'error');}
    finally{setImporting(false);}
  };

  const confirmImport = async () => {
    try{
      setImporting(true);
      const valid=(importPreview?.rows||[]).filter(r=>!(r.errors||[]).length);
      const result=await api(`${API}/import`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:activeSession.SessionID,rows:valid,CreatedByUserID:getUserId()})});
      setShowImportModal(false);
      setImportPreview(null);
      setImportFile(null);
      await refreshCandidatesAndStats();
      alert(`تم استيراد ${result.inserted||0} شخص`);
    }catch(e){alert(e.message,'error');}
    finally{setImporting(false);}
  };

  const allExportColumns = useMemo(() => {
    const cols = enabledFields.map(f=>[f.FieldKey,f.FieldName]);
    if(scoreFields.length) cols.push(['Evaluation','التقييم']);
    cols.push(['PassportOfficeSerial','رقم ملف الجواز']);
    return cols;
  },[enabledFields,scoreFields.length]);

  const openColumns = (action) => {
    setColumnAction(action);
    setSelectedColumns(new Set(allExportColumns.map(c=>c[0])));
    setShowColumnsModal(true);
  };

  const rowsForOutput = () => {
    const visible=displayedCandidates;
    return visibleSelectedIds.size ? visible.filter(c=>visibleSelectedIds.has(c.CandidateID)) : visible;
  };
  const outputColumnKeys = () => allExportColumns.filter(([key])=>selectedColumns.has(key)).map(([key])=>key);

  const doExport = async () => {
    try{
      const rows=rowsForOutput();
      if(!rows.length) throw new Error('لا توجد نتائج ظاهرة لتصديرها');
      const ids=rows.map(c=>c.CandidateID);
      const blob=await api(`${API}/export`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({sessionId:activeSession.SessionID,candidateIds:ids,columns:outputColumnKeys()})});
      const a=document.createElement('a');
      a.href=URL.createObjectURL(blob);
      a.download=`${activeSession.SessionTitle||'كشف الاختبارات'}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      setShowColumnsModal(false);
    }catch(e){alert(e.message,'error');}
  };

  const printValue = (c,key) => {
    if(key==='PassportOfficeSerial') return c.PassportOfficeSerial||'';
    if(key==='Evaluation') return c.Evaluation?.percent===null||c.Evaluation?.percent===undefined?'':`${c.Evaluation.score}/${c.Evaluation.max} (${c.Evaluation.percent}%)`;
    const f=enabledFields.find(x=>x.FieldKey===key);
    if(!f)return '';
    if(boolValue(f.IsSystem)){
      if(f.FieldKey==='DelegateID')return c.DelegateName||'';
      if(f.FieldKey==='StatusID')return c.StatusName||'';
      if(f.FieldKey==='PreviousCompanies')return (c.PreviousCompanies||[]).map(id=>companies.find(x=>String(x.CompanyID)===String(id))?.CompanyName||id).join('، ');
      if(f.FieldKey==='PhoneNumber')return candidatePhonesText(c);
      return c[f.FieldKey]??'';
    }
    const v=candidateCustomValue(c,f);
    if(f.FieldType==='checkbox') return v?'نعم':'لا';
    if(f.FieldType==='multiselect') return Array.isArray(v)?v.join('، '):'';
    return v;
  };

  const buildProfessionalTestPrintHtml = () => {
    const rows=rowsForOutput();
    const labels=new Map(allExportColumns);
    const cols=outputColumnKeys();
    if(!rows.length) throw new Error('لا توجد نتائج ظاهرة للطباعة');
    if(!cols.length) throw new Error('اختر عمودًا واحدًا على الأقل');

    const settings=imageExportSettings;
    const printOrientation=settings.printOrientation==='portrait'?'portrait':'landscape';
    const title=String(settings.customTitle||activeSession?.SessionTitle||'كشف الاختبارات').trim();
    const visibleColumnsCount=cols.length;
    let baseFontSize,cellFontSize,headerFontSize;
    if(visibleColumnsCount<=5){baseFontSize='18px';cellFontSize='16px';headerFontSize='18px';}
    else if(visibleColumnsCount<=10){baseFontSize='16px';cellFontSize='14px';headerFontSize='16px';}
    else if(visibleColumnsCount<=15){baseFontSize='14px';cellFontSize='12px';headerFontSize='14px';}
    else if(visibleColumnsCount<=20){baseFontSize='12px';cellFontSize='11px';headerFontSize='12px';}
    else{baseFontSize='11px';cellFontSize='10px';headerFontSize='11px';}

    const sortLabel=sortConfig.key
      ? `${labels.get(sortConfig.key)||enabledFields.find(f=>f.FieldKey===sortConfig.key)?.FieldName||sortConfig.key} (${sortConfig.direction==='desc'?'تنازلي':'تصاعدي'})`
      : '';
    const uiPage=Math.max(1,Number(candidatePagination.page||1));
    const uiTotalPages=Math.max(1,Number(candidatePagination.totalPages||1));
    const pageText=`${uiPage} / ${uiTotalPages}`;

    const tableHeaders=cols.map(k=>`<th>${escapeHtml(labels.get(k)||k)}</th>`).join('');
    const tableRows=rows.map((candidate)=>`<tr>${cols.map(k=>`<td>${escapeHtml(printValue(candidate,k)||'-')}</td>`).join('')}</tr>`).join('');

    const headerSection=settings.showHeader ? `
      <div class="report-header">
        <div class="company-name">نظام إدارة الاختبارات</div>
        <div class="report-title">${escapeHtml(title)}</div>
        ${sortLabel?`<div class="sort-box">مُرتب حسب: ${escapeHtml(sortLabel)}</div>`:''}
        <div class="report-date">
          الشركة: ${escapeHtml(activeSession?.CompanyName||'-')} |
          تاريخ الاختبار: ${escapeHtml(formatDate(activeSession?.TestDate))}
          ${settings.showPageNum?` | صفحة البيانات: ${escapeHtml(pageText)}`:''}
        </div>
      </div>` : (settings.customTitle ? `<div class="title-only">${escapeHtml(title)}</div>` : '');

    const summarySection=settings.showTotal ? `
      <div class="summary-section">
        <div class="summary-title">الملخص الإحصائي</div>
        <div style="text-align:center;padding:10px">
          <div class="summary-item">
            <div class="summary-label">إجمالي الأشخاص في الجزء المطبوع</div>
            <div class="summary-value">${rows.length}</div>
          </div>
        </div>
      </div>` : '';

    const standalonePage=settings.showPageNum && !settings.showHeader ? `
      <div class="page-badge">صفحة البيانات: ${escapeHtml(pageText)}</div>` : '';

    const footerSection=settings.showFooter ? `
      <div class="footer">
        تم إنشاء الكشف بتاريخ ${escapeHtml(new Date().toLocaleString('ar-EG'))}
        ${settings.showPageNum?` — صفحة البيانات ${escapeHtml(pageText)}`:''}
      </div>` : '';

    return `<!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>${escapeHtml(title)}</title>
        <style>
          @page{size:A4 ${printOrientation};margin:8mm 5mm}
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;direction:rtl;background:white;color:black;font-size:${baseFontSize};line-height:1.4}
          .print-container{max-width:100%;margin:0;background:white;padding:8px}
          .report-header{text-align:center;margin-bottom:20px;border-bottom:3px solid black;padding-bottom:15px}
          .company-name{font-size:32px;font-weight:bold;color:black;margin-bottom:8px}
          .report-title{font-size:24px;color:black;margin-bottom:8px;font-weight:bold}
          .title-only{text-align:center;font-size:26px;font-weight:bold;margin-bottom:18px;padding-bottom:10px;border-bottom:2px solid black}
          .report-date{font-size:16px;color:black}
          .sort-box{text-align:center;margin:15px 0;padding:10px;background:#f0f8ff;border:1px solid #4472C4;border-radius:5px;color:#2F75B5;font-weight:bold}
          .summary-section{background:white;border:2px solid black;border-radius:8px;padding:15px;margin-bottom:20px}
          .summary-title{font-size:24px;font-weight:bold;color:black;margin-bottom:15px;text-align:center;border-bottom:1px solid black;padding-bottom:8px}
          .summary-item{display:inline-block;background:white;padding:10px 20px;border:1px solid black;border-radius:6px;min-width:170px}
          .summary-label{font-weight:bold;color:black;margin-bottom:6px;font-size:14px}
          .summary-value{font-size:22px;font-weight:bold;color:black}
          .page-badge{display:block;width:max-content;max-width:100%;margin:0 auto 16px;padding:8px 18px;border:1px solid black;border-radius:6px;font-weight:bold;text-align:center}
          table{width:100%;border-collapse:collapse;background:white;border:2px solid black}
          th,td{padding:10px 6px;text-align:center;border:1px solid black;font-size:${cellFontSize};color:black;font-weight:bold;vertical-align:middle}
          th{background:#f0f0f0;font-size:${headerFontSize}}
          tr:nth-child(even){background:#f8f8f8}tr:nth-child(odd){background:white}
          .footer{margin-top:14px;text-align:center;border-top:1px solid black;padding-top:8px;font-size:12px}
          @media print{
            .print-container{padding:5px}
            th,td{padding:8px 4px;font-size:${cellFontSize};font-weight:bold}
            th{font-size:${headerFontSize}}
            @page{size:A4 ${printOrientation};margin:8mm 3mm}
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          ${headerSection}
          ${standalonePage}
          ${summarySection}
          <table><thead><tr>${tableHeaders}</tr></thead><tbody>${tableRows}</tbody></table>
          ${footerSection}
        </div>
        <script>window.onload=()=>{setTimeout(()=>window.print(),180);};</script>
      </body>
      </html>`;
  };

  const doPrint = () => {
    try{
      const html=buildProfessionalTestPrintHtml();
      const w=window.open('','_blank');
      if(!w){alert('المتصفح منع نافذة الطباعة المنبثقة','error');return;}
      w.document.open();
      w.document.write(html);
      w.document.close();
      setShowColumnsModal(false);
    }catch(e){alert(e.message,'error');}
  };

  const waitForExportRender = () => new Promise(resolve=>{
    requestAnimationFrame(()=>requestAnimationFrame(resolve));
  });

  const saveBlobFile = async (blob,fileName) => {
    const safeName=String(fileName||'كشف-الاختبارات.png').replace(/[\\/:*?"<>|]+/g,'-');
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url;
    a.download=safeName;
    a.style.display='none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(()=>URL.revokeObjectURL(url),1500);
  };

  const getImageExportLayoutSettings = (visibleColumnsCount) => {
    if(visibleColumnsCount<=5) return {headerFontSize:'16px',cellFontSize:'15px',titleFontSize:'26px',containerWidth:'900px'};
    if(visibleColumnsCount<=10) return {headerFontSize:'14px',cellFontSize:'13px',titleFontSize:'24px',containerWidth:'1100px'};
    if(visibleColumnsCount<=15) return {headerFontSize:'12px',cellFontSize:'11px',titleFontSize:'22px',containerWidth:'1300px'};
    if(visibleColumnsCount<=20) return {headerFontSize:'11px',cellFontSize:'10px',titleFontSize:'20px',containerWidth:'1500px'};
    return {headerFontSize:'10px',cellFontSize:'9px',titleFontSize:'18px',containerWidth:'1700px'};
  };

  const renderTestReportImageBlob = async ({headers,pageRows,theme,settings,title,totalRowsCount,pageNumber,totalPages}) => {
    const {headerFontSize,cellFontSize,titleFontSize,containerWidth}=getImageExportLayoutSettings(headers.length);
    const container=document.createElement('div');
    container.style.cssText=`
      width:${containerWidth};padding:30px;background:${theme.bg};
      font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;direction:rtl;color:#1a1a1a;
      font-size:${cellFontSize};line-height:1.6;position:fixed;left:0;top:0;transform:translateX(-110%);
      z-index:2147483000;pointer-events:none;box-sizing:border-box;
    `;

    const headerCells=headers.map(h=>`
      <th style="padding:14px 10px;background:${theme.headerBg};color:white;font-size:${headerFontSize};font-weight:bold;text-align:center;white-space:nowrap;border:1px solid ${theme.headerBorder};text-shadow:1px 1px 2px rgba(0,0,0,.2)">
        ${escapeHtml(h)}
      </th>`).join('');

    const rowsHtml=pageRows.map((row,rowIndex)=>{
      const cells=headers.map(h=>`
        <td style="padding:10px 8px;border:1px solid ${theme.cellBorder};font-size:${cellFontSize};font-weight:bold;text-align:center;color:#1a1a1a;background:${rowIndex%2===0?theme.rowOdd:theme.rowEven}">
          ${escapeHtml(row[h]??'-')}
        </td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    const headerSection=settings.showHeader?`
      <div style="margin-bottom:25px;padding:30px;background:${theme.cardBg};border-radius:15px;border:3px solid ${theme.cardBorder};box-shadow:0 4px 15px rgba(0,0,0,.15)">
        <div style="font-size:${titleFontSize};font-weight:bold;text-align:center;color:${theme.titleColor};margin-bottom:14px;text-shadow:1px 1px 2px rgba(0,0,0,.1)">📋 ${escapeHtml(title)}</div>
        <div style="font-size:13px;color:${theme.titleColor};text-align:center;font-weight:700;margin-bottom:18px">
          ${escapeHtml(activeSession?.CompanyName||'-')} • ${escapeHtml(formatDate(activeSession?.TestDate))}
        </div>
        ${(settings.showTotal||settings.showPageNum)?`
          <div style="display:flex;justify-content:space-around;align-items:center;gap:20px">
            ${settings.showTotal?`
              <div style="text-align:center;padding:20px 35px;background:white;border-radius:12px;border:3px solid ${theme.cardBorder};min-width:160px;box-shadow:0 3px 10px rgba(0,0,0,.1)">
                <div style="font-size:14px;color:${theme.titleColor};font-weight:700;margin-bottom:10px">إجمالي الأشخاص</div>
                <div style="font-size:32px;color:${theme.titleColor};font-weight:bold">${totalRowsCount}</div>
              </div>`:''}
            ${settings.showPageNum?`
              <div style="text-align:center;padding:20px 35px;background:white;border-radius:12px;border:3px solid ${theme.cardBorder};min-width:160px;box-shadow:0 3px 10px rgba(0,0,0,.1)">
                <div style="font-size:14px;color:${theme.titleColor};font-weight:700;margin-bottom:10px">الصفحة</div>
                <div style="font-size:32px;color:${theme.titleColor};font-weight:bold">${pageNumber} / ${totalPages}</div>
              </div>`:''}
          </div>`:''}
        <div style="font-size:13px;color:${theme.titleColor};text-align:center;margin-top:18px;font-weight:600">📅 تاريخ التصدير: ${escapeHtml(new Date().toLocaleString('ar-EG'))}</div>
      </div>
    `:(settings.customTitle?`<div style="text-align:center;margin-bottom:20px;font-size:${titleFontSize};font-weight:bold;color:${theme.titleColor}">📋 ${escapeHtml(title)}</div>`:'');

    const footerSection=settings.showFooter?`
      <div style="margin-top:25px;padding:20px;background:${theme.footerBg};border-radius:12px;text-align:center;border:3px solid ${theme.footerBorder};box-shadow:0 3px 12px rgba(0,0,0,.1)">
        <div style="font-size:15px;color:${theme.titleColor};font-weight:bold;margin-bottom:6px">💼 نظام إدارة الاختبارات</div>
        <div style="font-size:13px;color:${theme.titleColor};font-weight:600">صفحة ${pageNumber} من ${totalPages}</div>
      </div>`:'';

    container.innerHTML=`
      ${headerSection}
      <div style="background:white;border-radius:15px;overflow:hidden;border:3px solid ${theme.cardBorder};box-shadow:0 5px 20px rgba(0,0,0,.15)">
        <table style="width:100%;border-collapse:collapse;background:white"><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table>
      </div>
      ${footerSection}`;
    document.body.appendChild(container);
    await waitForExportRender();

    try{
      const canvas=await html2canvas(container,{
        backgroundColor:null,scale:2,useCORS:true,allowTaint:true,logging:false,
        scrollX:0,scrollY:0,windowWidth:container.scrollWidth,windowHeight:container.scrollHeight,
        width:container.scrollWidth,height:container.scrollHeight
      });
      const blob=await new Promise((resolve,reject)=>{
        if(canvas?.toBlob){
          canvas.toBlob(result=>result?resolve(result):reject(new Error('تعذر إنشاء ملف الصورة')),'image/png',0.95);
        }else{
          try{
            const dataUrl=canvas.toDataURL('image/png',0.95);
            fetch(dataUrl).then(r=>r.blob()).then(resolve).catch(reject);
          }catch(err){reject(err);}
        }
      });
      return blob;
    }finally{
      if(container.parentNode) container.parentNode.removeChild(container);
    }
  };

  const doImageExport = async () => {
    try{
      const rows=rowsForOutput();
      const labels=new Map(allExportColumns);
      const cols=outputColumnKeys();
      if(!rows.length) throw new Error('لا توجد نتائج ظاهرة لتصديرها كصورة');
      if(!cols.length) throw new Error('اختر عمودًا واحدًا على الأقل');

      const headers=cols.map(k=>labels.get(k)||k);
      const mappedRows=rows.map(candidate=>{
        const row={};
        cols.forEach((key,index)=>{row[headers[index]]=printValue(candidate,key)||'-';});
        return row;
      });

      const settings=imageExportSettings;
      const theme=TEST_REPORT_COLOR_THEMES[settings.colorTheme]||TEST_REPORT_COLOR_THEMES.blue;
      const maxRows=Math.max(1,Math.min(100,Number(settings.maxRowsPerImage)||25));
      const pages=[];
      for(let i=0;i<mappedRows.length;i+=maxRows) pages.push(mappedRows.slice(i,i+maxRows));
      const totalPages=Math.max(1,pages.length);
      const title=String(settings.customTitle||activeSession?.SessionTitle||'كشف الاختبارات').trim();
      const safeBase=String(activeSession?.SessionTitle||'كشف-الاختبارات').replace(/[\\/:*?"<>|]+/g,'-');

      for(let i=0;i<pages.length;i+=1){
        const blob=await renderTestReportImageBlob({
          headers,
          pageRows:pages[i],
          theme,
          settings,
          title,
          totalRowsCount:mappedRows.length,
          pageNumber:i+1,
          totalPages
        });
        const suffix=totalPages>1?`-صفحة-${i+1}-من-${totalPages}`:'';
        await saveBlobFile(blob,`${safeBase}${suffix}.png`);
        if(totalPages>1) await new Promise(resolve=>setTimeout(resolve,120));
      }

      setShowColumnsModal(false);
      alert(totalPages>1?`تم إنشاء ${totalPages} صور بنفس تنسيق التقارير المتقدمة`:'تم تصدير الصورة بنفس تنسيق التقارير المتقدمة');
    }catch(e){
      console.error('خطأ تصدير صورة كشف الاختبارات:',e);
      alert(e.message||'حدث خطأ أثناء إنشاء الصورة','error');
    }
  };


  const cleanContactPhoneNumber = (value) => {
    let phone=String(value??'').trim();
    if(!phone) return '';
    if(phone.startsWith('+2')) phone=phone.substring(2);
    phone=phone.replace(/[^\d]/g,'');
    return phone.length>=10 && phone.length<=15 ? phone : '';
  };

  const exportDisplayedContacts = async () => {
    try{
      const rows=rowsForOutput();
      if(!rows.length) throw new Error('لا توجد نتائج ظاهرة لتصدير جهات الاتصال');

      // جهة اتصال واحدة لكل شخص: الاسم - اختبارات - المندوب - الشركة
      // وتُحفظ أرقام الهاتف المتعددة داخل نفس جهة الاتصال بدل تكرار الاسم أو إضافة تفاصيل أخرى.
      const csvRows=[[
        'Name','Given Name','Additional Name','Family Name',
        'Phone 1 - Type','Phone 1 - Value',
        'Phone 2 - Type','Phone 2 - Value',
        'Phone 3 - Type','Phone 3 - Value'
      ]];
      let peopleWithPhones=0;
      let skippedWithoutPhones=0;

      rows.forEach(candidate=>{
        const phones=[...new Set(candidatePhoneNumbers(candidate).map(cleanContactPhoneNumber).filter(Boolean))].slice(0,3);
        if(!phones.length){skippedWithoutPhones+=1;return;}
        peopleWithPhones+=1;

        const personName=String(candidate.NameArabic||'غير محدد').trim()||'غير محدد';
        const delegateName=String(candidate.DelegateName||delegates.find(d=>String(d.DelegateID)===String(candidate.DelegateID))?.DelegateName||'غير محدد').trim()||'غير محدد';
        const companyName=String(activeSession?.CompanyName||companies.find(c=>String(c.CompanyID)===String(activeSession?.CompanyID))?.CompanyName||'غير محدد').trim()||'غير محدد';
        const contactName=`${personName} - اختبارات - ${delegateName} - ${companyName}`;

        const phoneCells=[];
        for(let i=0;i<3;i+=1){
          phoneCells.push(phones[i]?'Mobile':'',phones[i]||'');
        }
        csvRows.push([contactName,contactName,'','',...phoneCells]);
      });

      if(csvRows.length<=1) throw new Error('لا توجد أرقام هواتف صحيحة ضمن الأشخاص الظاهرين');
      const escapeCsv=(value)=>`"${String(value??'').replace(/"/g,'""')}"`;
      const csv='\uFEFF'+csvRows.map(row=>row.map(escapeCsv).join(',')).join('\r\n');
      const blob=new Blob([csv],{type:'text/csv;charset=utf-8'});
      const safeSession=String(activeSession?.SessionTitle||'كشف-الاختبارات').replace(/[\\/:*?"<>|]+/g,'-');
      const datePart=new Date().toISOString().slice(0,10);
      await saveBlobFile(blob,`جهات-اتصال-${safeSession}-${datePart}.csv`);
      alert(`تم تصدير ${peopleWithPhones} جهة اتصال${skippedWithoutPhones?` — وتم تخطي ${skippedWithoutPhones} بدون رقم صحيح`:''}`,'success');
    }catch(e){
      console.error('خطأ تصدير جهات اتصال الاختبارات:',e);
      alert(e.message||'تعذر تصدير جهات الاتصال','error');
    }
  };

  // قيم مؤقتة للتكرار الذكي القادم من صفحة الاختبارات فقط.
  // لا يتم توليد أي بيانات عشوائية سوى رقم الجواز والرقم القومي.
  // بقية الحقول تُرسل فقط إذا كانت موجودة فعلياً في بيانات شخص الاختبار.
  // هذه القيم لا تُحفظ في Passports ولا في TestCandidates؛ تُرسل في navigation state للتكرار الذكي فقط.
  const randomInt = (min,max) => Math.floor(Math.random()*(max-min+1))+min;
  const pad2 = (value) => String(value).padStart(2,'0');

  const getSyntheticBirthYear = (candidate) => {
    const currentYear=new Date().getFullYear();
    const customBirthYear=birthYearField ? Number(candidateCustomValue(candidate,birthYearField)) : NaN;
    if(Number.isInteger(customBirthYear) && customBirthYear>=1900 && customBirthYear<=currentYear) return customBirthYear;
    const age=Number(candidate?.Age);
    if(Number.isFinite(age) && age>=0 && age<=120) return Math.max(1900,currentYear-Math.floor(age));
    return randomInt(1970,2002);
  };

  const makeSyntheticNationalId = (candidate,usedIds) => {
    const today=new Date();
    const birthYear=getSyntheticBirthYear(candidate);
    // نختار شهر/يوم مرّ بالفعل في السنة الحالية، حتى يظل العمر الظاهر متوافقاً مع سنة الميلاد قدر الإمكان.
    let month=randomInt(1,today.getMonth()+1);
    let day=month===today.getMonth()+1 ? randomInt(1,Math.max(1,today.getDate())) : randomInt(1,28);
    const century=birthYear>=2000?'3':'2';
    const yy=pad2(birthYear%100);
    const mm=pad2(month);
    const dd=pad2(day);
    let nationalID='';
    do{
      // 99 مقصودة: تجعل الرقم يبدو طبيعياً بطول 14 رقماً، لكنه يفشل تحقق كود المحافظة في صفحة الجوازات.
      const serial=String(randomInt(0,9999)).padStart(4,'0');
      const last=String(randomInt(0,9));
      nationalID=`${century}${yy}${mm}${dd}99${serial}${last}`;
    }while(usedIds.has(nationalID));
    usedIds.add(nationalID);
    return nationalID;
  };

  const makeSyntheticPassportNumber = (usedNumbers) => {
    let passportNumber='';
    do{
      // نفس الشكل المطلوب في القوالب: A متبوعاً بـ 8 أرقام، مثال A39850213.
      passportNumber=`A${String(randomInt(0,99999999)).padStart(8,'0')}`;
    }while(usedNumbers.has(passportNumber));
    usedNumbers.add(passportNumber);
    return passportNumber;
  };

  const buildTestsSmartRepeatPassportData = (rows) => {
    const list=Array.isArray(rows)?rows.filter(Boolean):[];
    const usedNationalIds=new Set();
    const usedPassportNumbers=new Set();

    return list.map(candidate=>{
      const syntheticNationalID=makeSyntheticNationalId(candidate,usedNationalIds);
      const syntheticPassportNumber=makeSyntheticPassportNumber(usedPassportNumbers);
      return {
        PassportID:candidate.PassportID||null,
        OfficeSerial:candidate.PassportOfficeSerial||'',
        SerialNumber:candidate.PassportOfficeSerial||'',
        NameArabic:candidate.NameArabic||'',
        NameEnglish:'',
        // التوليد العشوائي محصور في هذين الحقلين فقط عند القدوم من الاختبارات.
        PassportNumber:candidate.PassportNumber||syntheticPassportNumber,
        NationalID:candidate.NationalID||syntheticNationalID,
        DateOfBirth:candidate.DateOfBirth||'',
        Age:candidate.Age??'',
        LicenseType:candidate.LicenseType||'',
        DelegateID:candidate.DelegateID||null,
        DelegateName:candidate.DelegateName||'',
        CompanyID:activeSession?.CompanyID||null,
        CompanyName:activeSession?.CompanyName||'',
        PhoneNumbers:candidatePhoneNumbers(candidate),
        PassportStatus:candidate.PassportStatus||'',
        PreviousCompanies:normalizeCandidatePreviousCompanies(candidate.PreviousCompanies),
        Notes:candidate.Notes||'',
        SourceTestCandidateID:candidate.CandidateID,
        SourceTestSessionID:activeSession?.SessionID||null,
        SourceTestSessionTitle:activeSession?.SessionTitle||'',
        SyntheticIdentityFields:['PassportNumber','NationalID'],
        SyntheticIdentitySource:'tests-smart-repeat'
      };
    });
  };

  // تكرار ذكي سريع لشخص واحد: يفتح نفس قائمة القوالب التي تظهر من إجراءات صفحة الجوازات.
  const openCandidateSmartRepeat = (candidate) => {
    try{
      if(!candidate) throw new Error('بيانات الشخص غير متاحة');
      const passportData=buildTestsSmartRepeatPassportData([candidate]);
      if(!passportData.length) throw new Error('تعذر تجهيز بيانات الشخص للتكرار الذكي');
      setSmartRepeatPassportData(passportData);
      setShowSmartRepeatModal(true);
    }catch(e){
      alert(e.message||'تعذر فتح التكرار الذكي السريع','error');
    }
  };

  const navigateTestsToSmartRepeat = () => {
    try{
      const rows=rowsForOutput();
      if(!rows.length) throw new Error('لا توجد نتائج ظاهرة لإرسالها إلى التكرار الذكي');
      const passportData=buildTestsSmartRepeatPassportData(rows);
      navigate('/smart-repeat',{state:{passportData,source:'tests',testSessionId:activeSession?.SessionID||null,testSessionTitle:activeSession?.SessionTitle||'',syntheticIdentityFields:['PassportNumber','NationalID']}});
    }catch(e){alert(e.message||'تعذر فتح التكرار الذكي','error');}
  };

  const exportDelegateReportsAsZip = async () => {
    try{
      const rows=rowsForOutput();
      const labels=new Map(allExportColumns);
      const cols=outputColumnKeys();
      if(!rows.length) throw new Error('لا توجد نتائج ظاهرة لتصدير صور المناديب');
      if(!cols.length) throw new Error('اختر عمودًا واحدًا على الأقل');

      const groups=new Map();
      rows.forEach(candidate=>{
        const delegateName=String(candidate.DelegateName||delegates.find(d=>String(d.DelegateID)===String(candidate.DelegateID))?.DelegateName||'بدون مندوب').trim()||'بدون مندوب';
        if(!groups.has(delegateName)) groups.set(delegateName,[]);
        groups.get(delegateName).push(candidate);
      });
      const delegateGroups=Array.from(groups.entries()).map(([delegateName,candidates])=>({delegateName,candidates})).sort((a,b)=>a.delegateName.localeCompare(b.delegateName,'ar'));
      if(!delegateGroups.length) throw new Error('لم يتم العثور على بيانات مناديب داخل النتائج الظاهرة');

      const headers=cols.map(k=>labels.get(k)||k);
      const settings=imageExportSettings;
      const theme=TEST_REPORT_COLOR_THEMES[settings.colorTheme]||TEST_REPORT_COLOR_THEMES.blue;
      const maxRows=Math.max(1,Math.min(100,Number(settings.maxRowsPerImage)||25));
      const zip=new JSZip();
      const baseTitle=String(settings.customTitle||activeSession?.SessionTitle||'كشف الاختبارات').trim();
      const datePart=new Date().toISOString().slice(0,10);

      setDelegateZipLoading(true);
      setDelegateZipProgress({current:0,total:delegateGroups.length,delegateName:''});

      for(let groupIndex=0;groupIndex<delegateGroups.length;groupIndex+=1){
        const group=delegateGroups[groupIndex];
        setDelegateZipProgress({current:groupIndex+1,total:delegateGroups.length,delegateName:group.delegateName});
        const mappedRows=group.candidates.map(candidate=>{
          const row={};
          cols.forEach((key,index)=>{row[headers[index]]=printValue(candidate,key)||'-';});
          return row;
        });
        const pages=[];
        for(let i=0;i<mappedRows.length;i+=maxRows) pages.push(mappedRows.slice(i,i+maxRows));
        const totalPages=Math.max(1,pages.length);
        const title=`${baseTitle} - المندوب: ${group.delegateName}`;
        const safeDelegate=String(group.delegateName||'بدون مندوب').replace(/[\\/:*?"<>|]+/g,'-');

        for(let pageIndex=0;pageIndex<pages.length;pageIndex+=1){
          const blob=await renderTestReportImageBlob({
            headers,
            pageRows:pages[pageIndex],
            theme,
            settings,
            title,
            totalRowsCount:mappedRows.length,
            pageNumber:pageIndex+1,
            totalPages
          });
          const suffix=totalPages>1?`-صفحة-${pageIndex+1}-من-${totalPages}`:'';
          zip.file(`${safeDelegate}${suffix}.png`,blob);
        }
      }

      const zipBlob=await zip.generateAsync({type:'blob',compression:'DEFLATE',compressionOptions:{level:5}});
      const safeSession=String(activeSession?.SessionTitle||'كشف-الاختبارات').replace(/[\\/:*?"<>|]+/g,'-');
      await saveBlobFile(zipBlob,`صور-المناديب-${safeSession}-${datePart}.zip`);
      setShowColumnsModal(false);
      alert(`تم إنشاء ملف ZIP لصور ${delegateGroups.length} مندوب بنجاح`,'success');
    }catch(e){
      console.error('خطأ تصدير صور مناديب الاختبارات ZIP:',e);
      alert(e.message||'تعذر إنشاء ملف ZIP للمناديب','error');
    }finally{
      setDelegateZipLoading(false);
      setDelegateZipProgress(null);
    }
  };

  const toggleSelected = (id) => setSelectedIds(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;});
  const selectAllVisible = () => setSelectedIds(prev=>{
    const ids=displayedCandidates.map(c=>c.CandidateID);
    const allSelected=ids.length>0&&ids.every(id=>prev.has(id));
    const next=new Set(prev);
    ids.forEach(id=>allSelected?next.delete(id):next.add(id));
    return next;
  });

  const applyConvertDefaults = (baseForm, settings = convertDefaultSettings) => {
    const safe = createSafeConvertDefaultSettings(settings);
    const next = { ...baseForm };
    const apply = (key) => {
      if (safe.enabled[key] && String(safe.values[key] ?? '').trim() !== '') next[key] = safe.values[key];
    };
    apply('CompanyID');
    apply('TravelDuration');
    apply('PassportStatus');
    apply('AbilityTest');
    if (passportProfessionMode === 'text') {
      apply('PassportProfessionText');
      if (safe.enabled.PassportProfessionText) next.PassportProfessionID = '';
    } else {
      apply('PassportProfessionID');
      if (safe.enabled.PassportProfessionID) next.PassportProfessionText = '';
    }
    apply('TravelProfessionID');
    apply('DelegateID');
    if (next.PassportStatus !== 'قديم شركات') next.PreviousCompanies = [];
    return next;
  };

  const openConvertSettings = () => {
    setConvertSettingsDraft(createSafeConvertDefaultSettings(convertDefaultSettings));
    setShowConvertSettingsModal(true);
  };

  const setConvertSettingEnabled = (key, enabled) => {
    setConvertSettingsDraft(prev => createSafeConvertDefaultSettings({
      ...prev,
      enabled:{...prev.enabled,[key]:enabled}
    }));
  };

  const setConvertSettingValue = (key, value) => {
    setConvertSettingsDraft(prev => createSafeConvertDefaultSettings({
      ...prev,
      values:{...prev.values,[key]:value}
    }));
  };

  const saveConvertDefaultSettings = () => {
    const cleaned = createSafeConvertDefaultSettings(convertSettingsDraft);
    setConvertDefaultSettings(cleaned);
    localStorage.setItem(TESTS_CONVERT_DEFAULTS_STORAGE_KEY, JSON.stringify(cleaned));
    setConvertForm(prev => applyConvertDefaults(prev, cleaned));
    setShowConvertSettingsModal(false);
    alert('تم حفظ ضبط نموذج التسجيل في الجوازات وتطبيقه');
  };

  const openConvert = (ids) => {
    setDuplicateRegisterQueue([]);
    const actual=ids.filter(Boolean);
    if(!actual.length){alert('حدد شخصًا واحدًا على الأقل','error');return;}
    const singleCandidate = actual.length===1 ? candidates.find(c=>String(c.CandidateID)===String(actual[0])) : null;
    setConvertIds(actual);
    const baseForm={
      CompanyID:String(activeSession.CompanyID||''),
      TravelDuration:'',
      AbilityTest:'',
      PassportProfessionID:'',
      PassportProfessionText:'',
      TravelProfessionID:'',
      PassportStatus:singleCandidate?.PassportStatus||'',
      PreviousCompanies:normalizeCandidatePreviousCompanies(singleCandidate?.PreviousCompanies),
      DelegateID:'',
      AllowDuplicatePhone:false
    };
    setConvertForm(applyConvertDefaults(baseForm));
    setShowConvertModal(true);
  };

  const convertOne = async (candidateId, allowDuplicatePhone=!!convertForm.AllowDuplicatePhone) => api(`${PASSPORT_API}/from-test`,{
    method:'POST',headers:{'Content-Type':'application/json'},
    body:JSON.stringify({...convertForm,CandidateID:candidateId,CreatedByUserID:getUserId(),allowDuplicatePhone,AllowDuplicatePhone:undefined})
  });

  const markCandidateAsRegistered = async (candidateId, passport, fromQueue=false) => {
    if (!can('تعليم كمسجل بالجوازات',['تسجيل في الجوازات'])) {
      alert('ليس لديك صلاحية تعليم الشخص بأنه مسجل في الجوازات','error');
      return;
    }
    const passportId = Number(passport?.PassportID || 0);
    if (!candidateId || !passportId) {
      alert('تعذر تحديد الجواز الموجود لربطه بالشخص','error');
      return;
    }
    try {
      setMarkingRegistered(true);
      const result = await api(`${API}/candidates/${candidateId}/mark-registered`,{
        method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({PassportID:passportId,UpdatedByUserID:getUserId()})
      });
      await refreshCandidatesAndStats();
      if (showRegisteredCheck) await checkRegisteredByPhone();
      alert(`تم تعليم الشخص بأنه مسجل بالفعل${result?.officeSerial?` — ملف ${result.officeSerial}`:''}`);
      if (fromQueue) {
        setDuplicateRegisterQueue(prev=>prev.slice(1));
        if (duplicateRegisterQueue.length <= 1) setShowConvertModal(false);
      }
    } catch (e) {
      alert(e.message,'error');
    } finally {
      setMarkingRegistered(false);
    }
  };

  const runConversion = async () => {
    try{
      if(!convertForm.CompanyID) throw new Error('اختر الشركة');
      if(!convertForm.TravelDuration) throw new Error('اختر مدة السفر');
      if(!convertForm.AbilityTest) throw new Error('اختر نتيجة اختبار القدرات');
      if(!convertForm.PassportStatus) throw new Error('اختر حالة الجواز: جديد أو قديم شركات');
      if(convertForm.PassportStatus==='قديم شركات' && !(convertForm.PreviousCompanies||[]).length) throw new Error('اختر شركة سابقة واحدة على الأقل للشخص القديم');
      if(passportProfessionMode==='select'&&!convertForm.PassportProfessionID)throw new Error('اختر المهنة بالجواز');
      if(passportProfessionMode==='text'&&!String(convertForm.PassportProfessionText||'').trim())throw new Error('اكتب المهنة بالجواز');
      if(!convertForm.TravelProfessionID)throw new Error('اختر مهنة السفر');
      if(convertForm.AllowDuplicatePhone && !can('تجاوز تكرار الهاتف')) throw new Error('ليس لديك صلاحية السماح بتكرار رقم الهاتف');

      setConverting(true);

      let ok=0,duplicates=[],failed=[];
      for(const id of convertIds){
        try{await convertOne(id,!!convertForm.AllowDuplicatePhone);ok++;}
        catch(e){
          if(e.status===409&&e.data?.code==='PHONE_DUPLICATE_FROM_TEST')duplicates.push({id,data:e.data});
          else failed.push({id,error:e.message});
        }
      }

      if(duplicates.length){
        // بدل إجبار المستخدم على إنشاء جواز مكرر: نعرض الجواز الموجود ويمكن تعليم الشخص بأنه مسجل بالفعل.
        setDuplicateRegisterQueue(duplicates);
      }

      await refreshCandidatesAndStats();
      if(ok>0)alert(`تم تسجيل ${ok} شخص في الجوازات${duplicates.length?` — ${duplicates.length} رقم مكرر لم يتم تسجيله`:''}${failed.length?` — ${failed.length} فشل`:''}`);
      if(duplicates.length||failed.length)console.warn('نتيجة التحويل',{duplicates,failed});
      if(!duplicates.length&&!failed.length)setShowConvertModal(false);
    }catch(e){alert(e.message,'error');}
    finally{setConverting(false);}
  };

  const openStatusHistory = async (c) => {
    try {
      setHistoryCandidate(c);
      setHistoryRows(readArray(await api(`${API}/candidates/${c.CandidateID}/status-history`)));
      setShowHistoryModal(true);
    } catch (e) { alert(e.message,'error'); }
  };

  const openPassport = (c) => {
    if(c.PassportOfficeSerial) navigate(`/passports?barcodeAction=edit&barcode=${c.PassportOfficeSerial}`);
  };

  const setCoreCandidateValue = (fieldKey, value) => setCandidateForm(prev=>({...prev,[fieldKey]:value}));
  const setCustomCandidateValue = (fieldKey, value, fieldType='') => setCandidateForm(prev=>{
    const next={...prev,CustomData:{...(prev.CustomData||{}),[fieldKey]:value}};
    if(fieldType==='birth_year') next.Age=calculateAgeFromBirthYear(value);
    return next;
  });

  const supportsRadioMode = (f) => {
    if(!f) return false;
    if(boolValue(f.IsSystem)) return ['LicenseType','TestResult','PassportStatus'].includes(f.FieldKey);
    return f.FieldType==='select';
  };

  const renderSingleChoiceInput = (field, value, onChange, options, placeholder='اختر...') => {
    const cleanOptions=(options||[]).map(String);
    const current=String(value??'');
    const allOptions=current && !cleanOptions.includes(current) ? [current,...cleanOptions] : cleanOptions;
    if((field.InputMode||'default')==='radio' && supportsRadioMode(field)){
      return <div className="flex flex-wrap gap-2">
        {!boolValue(field.IsRequired)&&<label className={`cursor-pointer px-3 py-2 rounded-xl border font-bold transition ${!current?'bg-slate-800 text-white border-slate-800':'bg-white hover:bg-slate-50'}`}><input type="radio" name={`field-${field.SessionFieldID}`} value="" checked={!current} onChange={()=>onChange('')} className="sr-only"/>بدون اختيار</label>}
        {allOptions.map(option=><label key={option} className={`cursor-pointer px-3 py-2 rounded-xl border font-bold transition ${current===option?'bg-blue-600 text-white border-blue-600':'bg-white hover:bg-blue-50 border-slate-300'}`}><input type="radio" name={`field-${field.SessionFieldID}`} value={option} checked={current===option} onChange={()=>onChange(option)} className="sr-only"/>{option}{!cleanOptions.includes(option)?' (قيمة قديمة)':''}</label>)}
      </div>;
    }
    if(!boolValue(field.IsSystem) && field.FieldType==='select') {
      return <div data-enter-save-skip="true"><SearchableSelect options={allOptions.map(option=>({value:option,label:`${option}${!cleanOptions.includes(option)?' (قيمة قديمة)':''}`}))} value={current} onChange={v=>onChange(v||'')} placeholder={placeholder} emptyMessage="لا توجد اختيارات"/></div>;
    }
    return <select value={current} onChange={e=>onChange(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:ring-2 focus:ring-blue-500"><option value="">{placeholder}</option>{allOptions.map(option=><option key={option} value={option}>{option}{!cleanOptions.includes(option)?' (قيمة قديمة)':''}</option>)}</select>;
  };

  const renderCandidateField = (f) => {
    if(f.FieldKey==='PreviousCompanies' && candidateForm.PassportStatus!=='قديم شركات') return null;
    const required=boolValue(f.IsRequired);
    const common="w-full px-3 py-2.5 rounded-xl border border-slate-300 outline-none focus:ring-2 focus:ring-blue-500";
    const wrapClass = f.FieldType==='textarea' || f.FieldKey==='StatusID' ? 'lg:col-span-3' : (f.FieldKey==='NameArabic' ? 'lg:col-span-2' : '');
    let content=null;

    if(boolValue(f.IsSystem)){
      switch(f.FieldKey){
        case 'NameArabic': content=<input autoFocus value={candidateForm.NameArabic||''} onChange={e=>setCoreCandidateValue('NameArabic',e.target.value)} className={common}/>; break;
        case 'PhoneNumber': {
          const phoneKeys=['PhoneNumber','PhoneNumber2','PhoneNumber3'];
          content=<div>
            {phoneKeys.slice(0,candidatePhoneCount).map((phoneKey,index)=><div key={phoneKey} className="flex gap-2 mb-2">
              <input
                value={candidateForm[phoneKey]||''}
                onChange={e=>setCoreCandidateValue(phoneKey,e.target.value)}
                className={`${common} flex-1`}
                dir="ltr"
                inputMode="tel"
                maxLength={15}
                autoComplete="off"
                placeholder={`رقم الهاتف ${index+1}`}
              />
              {index>0&&<button
                type="button"
                title="حذف رقم الهاتف"
                onClick={()=>{
                  const next={...candidateForm};
                  if(index===1){
                    next.PhoneNumber2=next.PhoneNumber3||'';
                    next.PhoneNumber3='';
                  }else{
                    next.PhoneNumber3='';
                  }
                  setCandidateForm(next);
                  setCandidatePhoneCount(Math.max(1,candidatePhoneCount-1));
                }}
                className="shrink-0 bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 p-2.5 rounded-xl"
              ><X size={16}/></button>}
            </div>)}
            {candidatePhoneCount<3 && String(candidateForm[phoneKeys[candidatePhoneCount-1]]||'').trim() && <button
              type="button"
              onClick={()=>setCandidatePhoneCount(count=>Math.min(3,count+1))}
              className="mt-1 text-blue-600 hover:text-blue-700 text-sm font-bold inline-flex items-center gap-1"
            ><Plus size={16}/> إضافة رقم هاتف آخر</button>}
          </div>;
          break;
        }
        case 'Age': content=birthYearField?<div><input type="number" value={candidateForm.Age??''} readOnly className={`${common} bg-slate-50 text-slate-600`} /><p className="text-xs text-blue-600 mt-1">يُحسب تلقائيًا من سنة الميلاد.</p></div>:<input type="number" value={candidateForm.Age??''} onChange={e=>setCoreCandidateValue('Age',e.target.value)} className={common}/>; break;
        case 'LicenseType': content=renderSingleChoiceInput(f,candidateForm.LicenseType||'',v=>setCoreCandidateValue('LicenseType',v),f.Options||[],'اختر نوع الرخصة...'); break;
        case 'DelegateID': content=<div data-enter-save-skip="true"><SearchableSelect options={delegateOptions} value={candidateForm.DelegateID||''} onChange={v=>setCoreCandidateValue('DelegateID',v)} placeholder="ابحث عن المندوب..."/></div>; break;
        case 'TestResult': {
          content=renderSingleChoiceInput(f,candidateForm.TestResult||'',v=>setCoreCandidateValue('TestResult',v),f.Options||[],'اختر نتيجة الاختبار...');
          break;
        }
        case 'PassportStatus': content=renderSingleChoiceInput(f,candidateForm.PassportStatus||'',v=>setCandidateForm(prev=>({...prev,PassportStatus:v,PreviousCompanies:v==='قديم شركات'?(prev.PreviousCompanies||[]):[]})),PASSPORT_STATUSES,'اختر حالة الجواز...'); break;
        case 'PreviousCompanies': content=<div data-enter-save-skip="true"><SearchableSelect options={companies.map(c=>({value:String(c.CompanyID),label:c.CompanyName,subtitle:`ID: ${c.CompanyID}`}))} multiple={true} selectedValues={(candidateForm.PreviousCompanies||[]).map(String)} onMultiChange={values=>setCoreCandidateValue('PreviousCompanies',values)} placeholder="ابحث واختر الشركات السابقة..." emptyMessage="لا توجد شركات"/></div>; break;
        case 'StatusID': content=<div className="flex flex-wrap gap-2"><button type="button" onClick={()=>setCoreCandidateValue('StatusID','')} className={`px-3 py-2 rounded-xl border font-bold ${!candidateForm.StatusID?'ring-2 ring-slate-400':''}`}>بدون حالة</button>{statuses.map(s=><button type="button" key={s.StatusID} onClick={()=>setCoreCandidateValue('StatusID',String(s.StatusID))} className={`px-3 py-2 rounded-xl border font-bold ${String(candidateForm.StatusID)===String(s.StatusID)?'ring-2':''}`} style={{backgroundColor:withAlpha(s.ColorHex,'16'),color:s.ColorHex,borderColor:withAlpha(s.ColorHex,'66')}}>{s.StatusName}</button>)}</div>; break;
        case 'Notes': content=<textarea rows="3" value={candidateForm.Notes||''} onChange={e=>setCoreCandidateValue('Notes',e.target.value)} className={common}/>; break;
        default: content=<input value={candidateForm[f.FieldKey]||''} onChange={e=>setCoreCandidateValue(f.FieldKey,e.target.value)} className={common}/>;
      }
    } else {
      const value=candidateCustomValue(candidateForm,f);
      if(f.FieldType==='select') content=renderSingleChoiceInput(f,value||'',v=>setCustomCandidateValue(f.FieldKey,v),f.Options||[],'اختر...');
      else if(f.FieldType==='multiselect') {
        const selected=Array.isArray(value)?value.map(String):[];
        content=<div className="flex flex-wrap gap-2">{(f.Options||[]).map(option=>{const checked=selected.includes(String(option));return <label key={option} className={`cursor-pointer flex items-center gap-2 px-3 py-2 rounded-xl border font-bold transition ${checked?'bg-blue-50 text-blue-700 border-blue-300':'bg-white hover:bg-slate-50 border-slate-300'}`}><input type="checkbox" checked={checked} onChange={e=>{const next=e.target.checked?[...selected,String(option)]:selected.filter(v=>v!==String(option));setCustomCandidateValue(f.FieldKey,next);}}/>{option}</label>;})}{!(f.Options||[]).length&&<span className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-xl">لا توجد اختيارات؛ أضفها من ضبط الحقول.</span>}</div>;
      }
      else if(f.FieldType==='checkbox') content=<label className="flex items-center gap-2 py-2 font-bold"><input type="checkbox" checked={!!value} onChange={e=>setCustomCandidateValue(f.FieldKey,e.target.checked)}/> نعم</label>;
      else if(f.FieldType==='textarea') content=<textarea rows="3" value={value??''} onChange={e=>setCustomCandidateValue(f.FieldKey,e.target.value)} className={common}/>;
      else if(f.FieldType==='score') content=<div className="relative"><input type="number" min="0" max={f.MaxScore||undefined} step="0.01" value={value??''} onChange={e=>setCustomCandidateValue(f.FieldKey,e.target.value)} className={`${common} pl-20`}/><span className="absolute left-3 top-3 text-xs font-bold text-slate-500">من {Number(f.MaxScore||10)}</span></div>;
      else if(f.FieldType==='birth_year') content=<div><input type="number" min="1900" max={new Date().getFullYear()} step="1" inputMode="numeric" value={value??''} onChange={e=>setCustomCandidateValue(f.FieldKey,e.target.value,'birth_year')} className={common} placeholder="مثال: 1988"/><p className="text-xs text-blue-600 mt-1">سيُحسب العمر تلقائيًا من سنة الميلاد.</p></div>;
      else content=<input type={f.FieldType==='number'?'number':f.FieldType==='date'?'date':'text'} value={value??''} onChange={e=>setCustomCandidateValue(f.FieldKey,e.target.value)} className={common}/>;
    }

    return <div key={f.SessionFieldID} className={wrapClass}><Field label={f.FieldName} required={required} hint={f.FieldType==='score'?`درجة من ${Number(f.MaxScore||10)}`:f.FieldType==='birth_year'?'أدخل السنة فقط؛ العمر المرتبط يُحسب تلقائيًا.':null}>{content}</Field></div>;
  };

  const renderTableCell = (c,f) => {
    if(boolValue(f.IsSystem)){
      if(f.FieldKey==='DelegateID') return c.DelegateName||'-';
      if(f.FieldKey==='StatusID') return <select disabled={!can('تغيير حالة الشخص',['إدارة الحالات'])} value={c.StatusID||''} onChange={e=>quickStatus(c.CandidateID,e.target.value)} className="px-2 py-2 rounded-xl border font-bold" style={{color:c.ColorHex||'#475569',borderColor:withAlpha(c.ColorHex,'66')}}><option value="">بدون حالة</option>{statuses.map(s=><option key={s.StatusID} value={s.StatusID}>{s.StatusName}</option>)}</select>;
      if(f.FieldKey==='PreviousCompanies') return (c.PreviousCompanies||[]).map(id=>companies.find(x=>String(x.CompanyID)===String(id))?.CompanyName||id).join(' • ')||'-';
      if(f.FieldKey==='PhoneNumber') return candidatePhoneNumbers(c).length ? <div className="space-y-1" dir="ltr">{candidatePhoneNumbers(c).map((phone,index)=><div key={`${phone}-${index}`} className="font-bold text-slate-700">{phone}</div>)}</div> : '-';
      return c[f.FieldKey]??'-';
    }
    const value=candidateCustomValue(c,f);
    if(f.FieldType==='checkbox')return value?'نعم':'لا';
    if(f.FieldType==='multiselect')return Array.isArray(value)&&value.length?value.join(' • '):'-';
    if(f.FieldType==='score')return value===''||value===null||value===undefined?'-':<span className="font-black text-violet-700">{value} / {Number(f.MaxScore||10)}</span>;
    return value??'-';
  };

  const openCandidatesWorkListPicker = (candidateIds = []) => {
    const wanted = new Set((candidateIds || []).map(Number));
    const rows = candidates.filter(c => wanted.has(Number(c.CandidateID)));
    const items = rows.map(c => ({ SourceType:'TEST', SourceID:Number(c.CandidateID), NameArabic:c.NameArabic || '' }));
    if (!items.length) {
      alert('حدد شخصاً واحداً على الأقل من الصفحة الحالية', 'error');
      return;
    }
    setWorkListPickerItems(items);
    setShowWorkListPicker(true);
  };

  const renderAdvancedFilterControl = (field) => {
    const kind=filterKindForField(field);
    const filter=advancedFilters[field.FieldKey];
    const common='w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white outline-none focus:ring-2 focus:ring-blue-500';

    if (kind==='select_multi' || kind==='multiselect') {
      const options=filterOptionsForField(field);
      const selected=Array.isArray(filter)?filter.map(String):[];
      return <Field key={field.SessionFieldID} label={field.FieldName} hint={field.FieldKey==='TestResult'?'يمكن اختيار أكثر من نتيجة؛ يكفي أن يطابق الشخص أي نتيجة مختارة.':'يمكن اختيار أكثر من قيمة.'}><SearchableSelect options={options} multiple={true} selectedValues={selected} onMultiChange={values=>updateAdvancedFilter(field.FieldKey,values)} placeholder={`اختر ${field.FieldName}...`} emptyMessage="لا توجد اختيارات"/></Field>;
    }

    if (kind==='select') {
      const options=filterOptionsForField(field);
      if (field.FieldKey==='DelegateID' || field.FieldKey==='PreviousCompanies') {
        return <Field key={field.SessionFieldID} label={field.FieldName}><SearchableSelect options={options} value={filter||''} onChange={v=>updateAdvancedFilter(field.FieldKey,v||'')} placeholder={`كل ${field.FieldName}`} emptyMessage="لا توجد نتائج"/></Field>;
      }
      return <Field key={field.SessionFieldID} label={field.FieldName}><select value={filter||''} onChange={e=>updateAdvancedFilter(field.FieldKey,e.target.value)} className={common}><option value="">الكل</option>{options.map(o=><option key={`${field.FieldKey}-${o.value}`} value={o.value}>{o.label}</option>)}</select></Field>;
    }

    if (kind==='checkbox') {
      return <Field key={field.SessionFieldID} label={field.FieldName}><select value={filter||''} onChange={e=>updateAdvancedFilter(field.FieldKey,e.target.value)} className={common}><option value="">الكل</option><option value="1">نعم</option><option value="0">لا</option></select></Field>;
    }

    if (kind==='range') {
      const value=(filter&&typeof filter==='object')?filter:{min:'',max:''};
      return <Field key={field.SessionFieldID} label={field.FieldName}><div className="grid grid-cols-2 gap-2"><input type="number" value={value.min??''} onChange={e=>updateAdvancedFilter(field.FieldKey,{...value,min:e.target.value})} className={common} placeholder="من"/><input type="number" value={value.max??''} onChange={e=>updateAdvancedFilter(field.FieldKey,{...value,max:e.target.value})} className={common} placeholder="إلى"/></div></Field>;
    }

    if (kind==='date') {
      const value=(filter&&typeof filter==='object')?filter:{from:'',to:''};
      return <Field key={field.SessionFieldID} label={field.FieldName}><div className="grid grid-cols-2 gap-2"><input type="date" value={value.from??''} onChange={e=>updateAdvancedFilter(field.FieldKey,{...value,from:e.target.value})} className={common}/><input type="date" value={value.to??''} onChange={e=>updateAdvancedFilter(field.FieldKey,{...value,to:e.target.value})} className={common}/></div></Field>;
    }

    return <Field key={field.SessionFieldID} label={field.FieldName}><input value={filter||''} onChange={e=>updateAdvancedFilter(field.FieldKey,e.target.value)} className={common} placeholder={`ابحث داخل ${field.FieldName}...`}/></Field>;
  };

  const sortIndicator = (fieldKey) => {
    if (sortConfig.key!==fieldKey) return <span className="text-slate-300 text-xs">↕</span>;
    return sortConfig.direction==='asc'?<ArrowUp size={14} className="text-blue-600"/>:<ArrowDown size={14} className="text-blue-600"/>;
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="flex items-center gap-3 text-slate-600 font-bold"><RefreshCw className="animate-spin"/> جارٍ تجهيز نظام الاختبارات...</div></div>;
  if (!can('عرض الاختبارات')) return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" dir="rtl"><div className="bg-white border rounded-3xl p-10 text-center max-w-lg"><AlertTriangle className="mx-auto text-amber-500 mb-3" size={40}/><div className="font-black text-xl">ليس لديك صلاحية عرض صفحة الاختبارات</div><button onClick={()=>navigate('/')} className="mt-5 px-4 py-2.5 bg-slate-900 text-white rounded-xl font-bold">العودة للرئيسية</button></div></div>;

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {message && <div className={`fixed top-5 left-1/2 -translate-x-1/2 z-[2000] px-5 py-3 rounded-2xl shadow-2xl font-bold max-w-[90vw] whitespace-pre-line ${message.type==='error'?'bg-red-600 text-white':'bg-emerald-600 text-white'}`}>{message.text}</div>}

      {showRegisteredCheck && <aside className="fixed inset-y-0 left-0 z-[1600] w-full sm:w-[470px] bg-white border-r shadow-2xl flex flex-col" dir="rtl">
        <div className="p-5 border-b bg-slate-900 text-white flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-black">المسجلون سابقًا بالهاتف</h2>
            <p className="text-xs text-slate-300 mt-1">الفحص يتم برقم الهاتف فقط داخل الجوازات النشطة، بدون مطابقة الاسم.</p>
          </div>
          <button onClick={()=>setShowRegisteredCheck(false)} className="p-2 rounded-xl bg-white/10 hover:bg-white/20"><X size={20}/></button>
        </div>
        <div className="p-4 border-b bg-slate-50">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-2xl bg-white border p-3"><div className="text-xs text-slate-500 font-bold">إجمالي الكشف</div><div className="text-xl font-black mt-1">{Number(registeredCheckResult?.totalCandidates||0).toLocaleString('ar-EG')}</div></div>
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3"><div className="text-xs text-emerald-700 font-bold">مسجلون سابقًا</div><div className="text-xl font-black text-emerald-700 mt-1">{Number(registeredCheckResult?.registeredCandidates||0).toLocaleString('ar-EG')}</div></div>
            <div className="rounded-2xl bg-blue-50 border border-blue-200 p-3"><div className="text-xs text-blue-700 font-bold">تم فحصهم بالهاتف</div><div className="text-xl font-black text-blue-700 mt-1">{Number(registeredCheckResult?.checkedCandidates||0).toLocaleString('ar-EG')}</div></div>
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3"><div className="text-xs text-amber-700 font-bold">بدون هاتف صالح</div><div className="text-xl font-black text-amber-700 mt-1">{Number(registeredCheckResult?.withoutPhone||0).toLocaleString('ar-EG')}</div></div>
          </div>
          <div className="flex gap-2">
            <div className="relative flex-1"><Search className="absolute right-3 top-3 text-slate-400" size={18}/><input value={registeredCheckSearch} onChange={e=>setRegisteredCheckSearch(e.target.value)} placeholder="ابحث داخل النتائج..." className="w-full pr-10 pl-3 py-2.5 rounded-xl border"/></div>
            <Button variant="light" disabled={checkingRegistered} onClick={checkRegisteredByPhone}>{checkingRegistered?<RefreshCw className="animate-spin" size={17}/>:<RefreshCw size={17}/>}</Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {checkingRegistered && !registeredCheckResult ? <div className="py-16 text-center text-slate-500 font-bold"><RefreshCw className="animate-spin mx-auto mb-3"/> جارٍ فحص أرقام الهاتف...</div> : visibleRegisteredMatches.length ? <div className="space-y-3">
            {visibleRegisteredMatches.map(item=><div key={item.CandidateID} className="border-2 border-emerald-100 rounded-2xl p-4 bg-emerald-50/30">
              <div className="flex items-start justify-between gap-3">
                <div><div className="font-black text-slate-900">{item.NameArabic}</div><div className="text-sm text-slate-600 mt-1" dir="ltr">{(item.PhoneNumbers||[item.PhoneNumber]).filter(Boolean).join(' / ')||'-'}</div>{(item.matchedPhones||[]).length>0&&<div className="text-xs text-emerald-700 font-bold mt-1" dir="ltr">المطابق: {item.matchedPhones.join(' / ')}</div>}</div>
                <span className="px-2.5 py-1 rounded-full bg-emerald-600 text-white text-xs font-black">مسجل</span>
              </div>
              <div className="mt-3 space-y-2">{(item.passports||[]).map(passport=><div key={passport.PassportID} className="bg-white border rounded-xl p-3">
                <div className="font-bold text-sm">{passport.NameArabic||'اسم غير مسجل'} — ملف {passport.OfficeSerial||passport.PassportID}</div>
                <div className="text-xs text-slate-500 mt-1">الجواز: {passport.PassportNumber||'-'} • الشركة: {passport.CompanyName||'-'} • الحالة: {passport.PassportStatus||'-'}</div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  {Number(item.LinkedPassportID)===Number(passport.PassportID)&&<span className="text-xs font-black text-blue-700 bg-blue-50 px-2 py-1 rounded-lg">مرتبط بالفعل من الكشف</span>}
                  {!item.LinkedPassportID&&can('تعليم كمسجل بالجوازات',['تسجيل في الجوازات'])&&<button disabled={markingRegistered} onClick={()=>markCandidateAsRegistered(item.CandidateID,passport,false)} className="inline-flex items-center gap-1 text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg hover:bg-emerald-100 disabled:opacity-50"><CheckCircle2 size={13}/> تعليم كمسجل</button>}
                  {can('فتح الجواز المرتبط',['عرض الاختبارات'])&&<button onClick={()=>navigate(`/passports?barcodeAction=edit&barcode=${encodeURIComponent(passport.OfficeSerial||passport.PassportID)}`)} className="mr-auto inline-flex items-center gap-1 text-xs font-black text-blue-700 hover:text-blue-900">فتح الجواز <ExternalLink size={13}/></button>}
                </div>
              </div>)}</div>
            </div>)}
          </div> : registeredCheckResult ? <div className="py-16 text-center"><CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={42}/><div className="font-black text-slate-800">لا توجد أسماء مسجلة سابقًا بهذه الأرقام</div><div className="text-sm text-slate-500 mt-2">تمت المقارنة مع الجوازات النشطة باستخدام رقم الهاتف فقط.</div></div> : null}
        </div>
      </aside>}

      <div className="max-w-[1800px] mx-auto p-4 md:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <button onClick={()=>activeSession?setActiveSession(null):navigate('/')} className="p-2.5 bg-white border rounded-xl hover:bg-slate-50"><ArrowRight size={20}/></button>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900">{activeSession?activeSession.SessionTitle:'إدارة الاختبارات'}</h1>
              <p className="text-sm text-slate-500 mt-1">{activeSession?'الحقول والنتائج والتقييمات في هذا الكشف مستقلة عن باقي الكشوف.':'كشوف منظمة لكل شركة وسنة مع استيراد وتصدير وربط بالجوازات.'}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!activeSession && can('إنشاء كشف') && <Button onClick={openNewSession}><Plus size={18}/> كشف جديد</Button>}
            {activeSession && can('إنشاء نسخة فارغة من كشف',['إنشاء كشف']) && <Button variant="light" onClick={()=>openCloneSession(activeSession)}><Copy size={18}/> نسخة فارغة</Button>}
            {activeSession && can('إضافة شخص',['إضافة وتعديل الأشخاص']) && <Button onClick={()=>openCandidate()}><UserPlus size={18}/> إضافة شخص</Button>}
            {activeSession && can('فحص المسجلين بالهاتف') && <Button variant="light" disabled={checkingRegistered} onClick={checkRegisteredByPhone}>{checkingRegistered?<RefreshCw className="animate-spin" size={18}/>:<Search size={18}/>} فحص المسجلين بالهاتف</Button>}
            {activeSession && can('استيراد') && <Button variant="light" onClick={()=>{setShowImportModal(true);setImportPreview(null);setImportFile(null);}}><Upload size={18}/> استيراد</Button>}
            {activeSession && can('تصدير Excel',['تصدير وطباعة']) && <Button variant="light" onClick={()=>openColumns('export')}><Download size={18}/> تصدير</Button>}
            {activeSession && can('طباعة',['تصدير وطباعة']) && <Button variant="light" onClick={()=>openColumns('print')}><Printer size={18}/> طباعة</Button>}
            {activeSession && can('تصدير كصورة',['تصدير وطباعة']) && <Button variant="light" onClick={()=>openColumns('image')}><Image size={18}/> تصدير صورة</Button>}
            {activeSession && can('تصدير صور المناديب ZIP',['تصدير وطباعة']) && <Button variant="light" onClick={()=>openColumns('delegateZip')} disabled={delegateZipLoading}><Image size={18}/>{delegateZipLoading?(delegateZipProgress?`مناديب ${delegateZipProgress.current}/${delegateZipProgress.total}`:'تجهيز ZIP...'):'تصدير صور المناديب ZIP'}</Button>}
            {activeSession && can('تصدير جهات الاتصال',['تصدير وطباعة']) && <Button variant="light" onClick={exportDisplayedContacts}><Phone size={18}/> تصدير جهات الاتصال</Button>}
            {activeSession && can('التكرار الذكي',['تصدير وطباعة']) && <Button variant="light" onClick={navigateTestsToSmartRepeat} disabled={!displayedCandidates.length}><Repeat size={18}/> التكرار الذكي</Button>}
            {activeSession && can('إدارة الحقول') && <Button variant="light" onClick={()=>{setShowFieldsModal(true);setShowFieldEditor(false);}}><SlidersHorizontal size={18}/> تخصيص الحقول</Button>}
            {can('إدارة الحالات') && <Button variant="light" onClick={()=>setShowStatusModal(true)}><Palette size={18}/> الحالات</Button>}
          </div>
        </div>

        {!activeSession ? (
          <>
            <div className="bg-white border rounded-3xl p-4 shadow-sm mb-5">
              <div className="grid md:grid-cols-4 gap-3">
                <div className="md:col-span-2 relative"><Search className="absolute right-3 top-3 text-slate-400" size={20}/><input disabled={!can('البحث والفلترة',['عرض الاختبارات'])} value={sessionSearch} onChange={e=>setSessionSearch(e.target.value)} placeholder="ابحث باسم الكشف أو الشركة..." className="w-full pr-11 pl-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-blue-500"/></div>
                <input disabled={!can('البحث والفلترة',['عرض الاختبارات'])} type="number" value={yearFilter} onChange={e=>setYearFilter(e.target.value)} placeholder="السنة" className="px-3 py-3 border rounded-xl"/>
                <SearchableSelect disabled={!can('البحث والفلترة',['عرض الاختبارات'])} options={companyOptions} value={companyFilter} onChange={setCompanyFilter} placeholder="كل الشركات"/>
              </div>
              {can('البحث العام',['عرض الاختبارات'])&&<div className="mt-4"><Field label="بحث عام عن شخص في كل الاختبارات"><div className="relative"><Search className="absolute right-3 top-3 text-slate-400" size={20}/><input value={globalSearch} onChange={e=>setGlobalSearch(e.target.value)} placeholder="اكتب الاسم أو رقم الهاتف..." className="w-full pr-11 pl-4 py-3 rounded-xl border"/></div></Field>
                {globalResults.length>0 && <div className="mt-3 divide-y border rounded-2xl overflow-hidden">{globalResults.map(r=><button key={r.CandidateID} onClick={async()=>{try{const s=await api(`${API}/sessions/${r.SessionID}`);openSession(s);}catch(e){alert(e.message,'error');}}} className="w-full p-3 text-right hover:bg-slate-50 flex items-center justify-between gap-3"><div><div className="font-black">{r.NameArabic}</div><div className="text-xs text-slate-500">{candidatePhonesText(r)||'بدون هاتف'} • {r.CompanyName||'-'} • {formatDate(r.TestDate)}</div></div><span className="px-3 py-1 rounded-full text-xs font-bold" style={{backgroundColor:withAlpha(r.ColorHex,'20'),color:r.ColorHex||'#475569'}}>{r.StatusName||r.TestResult||'بدون حالة'}</span></button>)}</div>}
              </div>}
            </div>

            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sessions.map(s=><div key={s.SessionID} className="bg-white border rounded-3xl p-5 shadow-sm hover:shadow-md transition">
                <button onClick={()=>openSession(s)} className="w-full text-right"><div className="flex items-start justify-between gap-3"><div><h3 className="font-black text-lg text-slate-900">{s.SessionTitle}</h3><div className="text-sm text-slate-500 mt-1">{s.TestType}</div></div><ChevronLeft className="text-slate-400"/></div>
                  <div className="grid grid-cols-2 gap-2 mt-4 text-sm"><div className="bg-slate-50 rounded-xl p-3"><CalendarDays className="inline ml-1" size={15}/>{formatDate(s.TestDate)}</div><div className="bg-slate-50 rounded-xl p-3"><Users className="inline ml-1" size={15}/>{s.CandidatesCount||0} شخص</div><div className="bg-slate-50 rounded-xl p-3 col-span-2"><Building2 className="inline ml-1" size={15}/>{s.CompanyName||'بدون شركة'}</div>{s.TestLocation&&<div className="bg-slate-50 rounded-xl p-3 col-span-2"><MapPin className="inline ml-1" size={15}/>{s.TestLocation}</div>}</div>
                </button>
                <div className="flex justify-end gap-1 mt-3 border-t pt-3">{can('إنشاء نسخة فارغة من كشف',['إنشاء كشف'])&&<button title="إنشاء نسخة فارغة بنفس الحقول والترتيب" onClick={()=>openCloneSession(s)} className="p-2 rounded-lg hover:bg-violet-50 text-violet-600"><Copy size={16}/></button>}{can('تعديل الكشوف')&&<button title="تعديل الكشف" onClick={()=>editSession(s)} className="p-2 rounded-lg hover:bg-blue-50 text-blue-600"><Edit2 size={16}/></button>}{can('حذف الكشوف')&&<button title="حذف الكشف" onClick={()=>deleteSession(s)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16}/></button>}</div>
              </div>)}
              {!sessions.length&&<div className="md:col-span-2 xl:col-span-3 bg-white border rounded-3xl p-12 text-center text-slate-500">لا توجد كشوف مطابقة.</div>}
            </div>
          </>
        ) : (
          <>
            {can('عرض إحصائيات الكشف',['عرض الاختبارات'])&&<div className={`grid gap-3 mb-5 ${scoreFields.length?'grid-cols-2 md:grid-cols-4 xl:grid-cols-7':'grid-cols-2 md:grid-cols-3 xl:grid-cols-6'}`}>
              <StatCard title="إجمالي الأشخاص" value={stats.Total} onClick={()=>{setCandidateStatusFilter('');setCandidateLinkedFilter('');}}/>
              <StatCard title="مسجل بالجوازات" value={stats.Linked} color="#16A34A" onClick={()=>setCandidateLinkedFilter('1')}/>
              <StatCard title="لم يُسجل بالجوازات" value={stats.NotLinked} color="#F59E0B" onClick={()=>setCandidateLinkedFilter('0')}/>
              {scoreFields.length>0&&<StatCard title={`متوسط التقييم (${stats.EvaluatedCount||0})`} value={stats.AverageEvaluation} suffix={stats.AverageEvaluation!==null?'%':''} color="#7C3AED"/>}
              {(stats.byStatus||[]).slice(0,3).map(s=><StatCard key={s.StatusID||s.StatusName} title={s.StatusName} value={s.Count} color={s.ColorHex} onClick={()=>{setCandidatePage(1);setCandidateStatusFilter(s.StatusID||'');}}/>) }
            </div>}

            <div className="bg-white border rounded-3xl shadow-sm overflow-hidden">
              <div className="p-4 border-b grid md:grid-cols-6 gap-3">
                <div className="md:col-span-2 relative"><Search className="absolute right-3 top-3 text-slate-400" size={20}/><input disabled={!can('البحث والفلترة',['عرض الاختبارات'])} value={candidateSearch} onChange={e=>{setCandidatePage(1);setCandidateSearch(e.target.value);}} placeholder="ابحث في بيانات الأشخاص..." className="w-full pr-11 pl-4 py-3 rounded-xl border outline-none focus:ring-2 focus:ring-blue-500"/></div>
                <select disabled={!can('البحث والفلترة',['عرض الاختبارات'])} value={candidateStatusFilter} onChange={e=>{setCandidatePage(1);setCandidateStatusFilter(e.target.value);}} className="px-3 py-3 border rounded-xl"><option value="">كل الحالات</option>{statuses.map(s=><option key={s.StatusID} value={s.StatusID}>{s.StatusName}</option>)}</select>
                <select disabled={!can('البحث والفلترة',['عرض الاختبارات'])} value={candidateLinkedFilter} onChange={e=>{setCandidatePage(1);setCandidateLinkedFilter(e.target.value);}} className="px-3 py-3 border rounded-xl"><option value="">الكل</option><option value="1">مسجل بالجوازات</option><option value="0">غير مسجل</option></select>
                {can('البحث والفلترة',['عرض الاختبارات'])&&<Button variant="light" onClick={()=>setShowAdvancedFilters(v=>!v)}><SlidersHorizontal size={17}/> فلاتر متقدمة{activeAdvancedFilterCount>0?` (${activeAdvancedFilterCount})`:''}</Button>}
                {can('تحديث البيانات',['عرض الاختبارات'])&&<Button variant="light" onClick={refreshCandidatesAndStats}><RefreshCw size={17}/> تحديث</Button>}
              </div>

              {showAdvancedFilters&&<div className="p-4 border-b bg-slate-50">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <div><div className="font-black text-slate-900">فلترة متقدمة حسب حقول الكشف</div><div className="text-xs text-slate-500 mt-1">يمكنك الجمع بين أكثر من فلتر. إجمالي النتائج المطابقة: {candidatePagination.total.toLocaleString('ar-EG')} — يتم تحميل {CANDIDATE_PAGE_SIZE} سجل فقط في كل صفحة.</div></div>
                  <div className="flex gap-2">{activeAdvancedFilterCount>0&&<Button variant="light" onClick={clearAdvancedFilters}>مسح الفلاتر</Button>}<Button variant="light" onClick={()=>setShowAdvancedFilters(false)}>إخفاء</Button></div>
                </div>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{enabledFields.map(renderAdvancedFilterControl)}</div>
              </div>}

              <div className="px-4 py-3 border-b bg-white flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-slate-600">
                  {candidatePagination.total>0 ? <><span className="font-black text-slate-900">{candidatePagination.from.toLocaleString('ar-EG')}–{candidatePagination.to.toLocaleString('ar-EG')}</span> من <span className="font-black text-slate-900">{candidatePagination.total.toLocaleString('ar-EG')}</span> نتيجة</> : <span className="font-black text-slate-900">لا توجد نتائج</span>}
                  <span className="text-slate-400"> • {CANDIDATE_PAGE_SIZE} سجل لكل صفحة • اضغط على عنوان أي عمود للترتيب</span>
                  {sortConfig.key&&<span> • مرتبة حسب <strong>{enabledFields.find(f=>f.FieldKey===sortConfig.key)?.FieldName||sortConfig.key}</strong> ({sortConfig.direction==='asc'?'تصاعدي':'تنازلي'})</span>}
                </div>
                {sortConfig.key&&<button onClick={()=>{setCandidatePage(1);setSortConfig({key:'',direction:'asc'});}} className="text-xs font-bold text-blue-600 hover:text-blue-800">إلغاء الترتيب</button>}
              </div>

              {visibleSelectedIds.size>0&&<div className="px-4 py-3 bg-blue-50 border-b flex items-center justify-between gap-3 flex-wrap"><span className="font-black text-blue-900">تم تحديد {visibleSelectedIds.size} شخص من النتائج الظاهرة</span><div className="flex gap-2 flex-wrap">{canAddToWorkList&&<Button variant="light" onClick={()=>openCandidatesWorkListPicker([...visibleSelectedIds])}><ListPlus size={17}/> إضافة لقائمة عمل</Button>}{can('تسجيل في الجوازات')&&<Button variant="success" onClick={()=>openConvert([...visibleSelectedIds])}><UserPlus size={17}/> تسجيل المحددين في الجوازات</Button>}</div></div>}

              <div id="tests-candidates-table" className="overflow-auto relative scroll-mt-4">
                {candidatesLoading&&<div className="absolute inset-x-0 top-0 z-20 h-1 bg-blue-100 overflow-hidden"><div className="h-full w-1/3 bg-blue-600 animate-pulse"/></div>}
                <table className={`w-full min-w-max text-sm transition-opacity ${candidatesLoading?'opacity-60':'opacity-100'}`}>
                  <thead className="bg-slate-100 text-slate-700"><tr><th className="p-3"><input type="checkbox" disabled={!can('التحديد المتعدد',['عرض الاختبارات'])} checked={displayedCandidates.length>0&&displayedCandidates.every(c=>selectedIds.has(c.CandidateID))} onChange={selectAllVisible}/></th><th className="p-3 text-right">م</th>{tableFields.map(f=><th key={f.SessionFieldID} className="p-3 text-right"><button type="button" onClick={()=>can('الترتيب',['عرض الاختبارات'])&&toggleSort(f.FieldKey)} className="inline-flex items-center gap-1.5 font-black hover:text-blue-700" title={`ترتيب حسب ${f.FieldName}`}>{f.FieldName}{sortIndicator(f.FieldKey)}</button></th>)}<th className="p-3 text-right">الجواز</th><th className="p-3 text-right">إجراءات</th></tr></thead>
                  <tbody className="divide-y">
                    {displayedCandidates.map((c,index)=><tr key={c.CandidateID} className="hover:bg-slate-50" style={{backgroundColor:withAlpha(c.ColorHex,'08')}}>
                      <td className="p-3"><input type="checkbox" disabled={!can('التحديد المتعدد',['عرض الاختبارات'])} checked={selectedIds.has(c.CandidateID)} onChange={()=>toggleSelected(c.CandidateID)}/></td>
                      <td className="p-3 font-bold">{((candidatePagination.page-1)*candidatePagination.pageSize)+index+1}</td>
                      {tableFields.map(f=><td key={f.SessionFieldID} className="p-3"><div>{renderTableCell(c,f)}</div>{f.FieldKey==='NameArabic'&&scoreFields.length>0&&c.Evaluation?.percent!==null&&c.Evaluation?.percent!==undefined&&<div className="mt-1 text-xs font-black text-violet-700 flex items-center gap-1"><Star size={12}/> التقييم {c.Evaluation.score}/{c.Evaluation.max} ({c.Evaluation.percent}%)</div>}</td>)}
                      <td className="p-3">{c.PassportID?(can('فتح الجواز المرتبط',['عرض الاختبارات'])?<button onClick={()=>openPassport(c)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 font-bold"><CheckCircle2 size={15}/> ملف {c.PassportOfficeSerial||c.PassportID}<ExternalLink size={13}/></button>:<span className="text-slate-400">مسجل</span>):<span className="text-slate-400">غير مسجل</span>}</td>
                      <td className="p-3"><div className="flex gap-1">{can('تعديل الأشخاص',['إضافة وتعديل الأشخاص'])&&<button onClick={()=>openCandidate(c)} className="p-2 rounded-lg hover:bg-blue-50 text-blue-600"><Edit2 size={16}/></button>}{canAddToWorkList&&<button title="إضافة لقائمة عمل" onClick={()=>openCandidatesWorkListPicker([c.CandidateID])} className="p-2 rounded-lg hover:bg-cyan-50 text-cyan-700"><ListPlus size={16}/></button>}{can('تسجيل في الجوازات')&&!c.PassportID&&<button title="تسجيل في الجوازات" onClick={()=>openConvert([c.CandidateID])} className="p-2 rounded-lg hover:bg-emerald-50 text-emerald-600"><UserPlus size={16}/></button>}{can('التكرار الذكي',['تصدير وطباعة'])&&<button title="تكرار ذكي سريع" onClick={()=>openCandidateSmartRepeat(c)} className="p-2 rounded-lg hover:bg-purple-50 text-purple-600"><Repeat size={16}/></button>}{can('عرض سجل الحالات',['عرض الاختبارات'])&&<button title="سجل الحالات" onClick={()=>openStatusHistory(c)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><Clock3 size={16}/></button>}{can('حذف الأشخاص')&&<button onClick={()=>deleteCandidate(c)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16}/></button>}</div></td>
                    </tr>)}
                    {!displayedCandidates.length&&<tr><td colSpan={tableFields.length+4} className="p-12 text-center text-slate-500">لا توجد نتائج مطابقة للفلاتر الحالية.</td></tr>}
                  </tbody>
                </table>
              </div>

              {candidatePagination.totalPages>1&&<div className="px-4 py-4 border-t bg-white flex items-center justify-between gap-3 flex-wrap" dir="rtl">
                <div className="text-xs text-slate-500 font-bold">صفحة {candidatePagination.page.toLocaleString('ar-EG')} من {candidatePagination.totalPages.toLocaleString('ar-EG')}</div>
                <div className="flex items-center gap-1.5 flex-wrap justify-center">
                  <button disabled={!candidatePagination.hasPrevious||candidatesLoading} onClick={()=>goToCandidatePage(1)} className="px-3 py-2 rounded-xl border text-sm font-bold disabled:opacity-35 hover:bg-slate-50">الأولى</button>
                  <button disabled={!candidatePagination.hasPrevious||candidatesLoading} onClick={()=>goToCandidatePage(candidatePagination.page-1)} className="px-3 py-2 rounded-xl border text-sm font-bold disabled:opacity-35 hover:bg-slate-50">السابق</button>
                  {paginationItems.map(item=>typeof item==='number'
                    ? <button key={item} disabled={candidatesLoading} onClick={()=>goToCandidatePage(item)} className={`min-w-10 px-3 py-2 rounded-xl border text-sm font-black ${item===candidatePagination.page?'bg-blue-600 text-white border-blue-600':'bg-white hover:bg-blue-50 text-slate-700'}`}>{item.toLocaleString('ar-EG')}</button>
                    : <span key={item} className="px-1 text-slate-400">…</span>)}
                  <button disabled={!candidatePagination.hasNext||candidatesLoading} onClick={()=>goToCandidatePage(candidatePagination.page+1)} className="px-3 py-2 rounded-xl border text-sm font-bold disabled:opacity-35 hover:bg-slate-50">التالي</button>
                  <button disabled={!candidatePagination.hasNext||candidatesLoading} onClick={()=>goToCandidatePage(candidatePagination.totalPages)} className="px-3 py-2 rounded-xl border text-sm font-bold disabled:opacity-35 hover:bg-slate-50">الأخيرة</button>
                </div>
              </div>}
            </div>
          </>
        )}
      </div>

      <Modal open={showSessionModal} onClose={()=>{setShowSessionModal(false);setCloneSourceSession(null);}} title={editingSession?'تعديل كشف الاختبار':cloneSourceSession?'إنشاء نسخة فارغة من الكشف':'إنشاء كشف اختبار جديد'} width="max-w-3xl" footer={<div className="flex justify-end gap-2"><Button variant="light" onClick={()=>{setShowSessionModal(false);setCloneSourceSession(null);}}>إلغاء</Button><Button onClick={saveSession}><Save size={17}/> {cloneSourceSession?'إنشاء النسخة':'حفظ'}</Button></div>}>
        {cloneSourceSession&&<div className="mb-4 p-4 rounded-2xl bg-violet-50 border border-violet-200 text-violet-900"><div className="font-black flex items-center gap-2"><Copy size={18}/> نسخة فارغة من: {cloneSourceSession.SessionTitle}</div><div className="text-sm mt-1">سيتم نسخ الحقول الحالية فقط بنفس الترتيب والاختيارات وطريقة العرض والتقييم، بدون نسخ أي شخص أو نتيجة أو حالة. بيانات الكشف الجديد أدناه قابلة للتعديل قبل الإنشاء.</div></div>}
        <div className="grid md:grid-cols-2 gap-4"><div className="md:col-span-2"><Field label="اسم الكشف" hint="يمكن تركه فارغاً ليُنشئ النظام الاسم تلقائياً"><input value={sessionForm.SessionTitle} onChange={e=>setSessionForm({...sessionForm,SessionTitle:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border"/></Field></div><Field label="الشركة" required><SearchableSelect options={companyOptions} value={sessionForm.CompanyID} onChange={v=>setSessionForm({...sessionForm,CompanyID:v})} placeholder="اختر الشركة..."/></Field><Field label="نوع الاختبار"><input value={sessionForm.TestType} onChange={e=>setSessionForm({...sessionForm,TestType:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border"/></Field><Field label="تاريخ الاختبار" required><input type="date" value={sessionForm.TestDate} onChange={e=>setSessionForm({...sessionForm,TestDate:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border"/></Field><Field label="مكان الاختبار"><input value={sessionForm.TestLocation} onChange={e=>setSessionForm({...sessionForm,TestLocation:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border"/></Field><div className="md:col-span-2"><Field label="ملاحظات"><textarea rows="3" value={sessionForm.Notes} onChange={e=>setSessionForm({...sessionForm,Notes:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border"/></Field></div></div>
      </Modal>

      <Modal open={showCandidateModal} onClose={()=>!candidateSaving&&setShowCandidateModal(false)} title={editingCandidate?'تعديل بيانات الشخص':'إضافة شخص للاختبار'} width="max-w-5xl" footer={<div className="flex items-center justify-between gap-3 flex-wrap"><label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={saveAndNext} disabled={!!editingCandidate||candidateSaving} onChange={e=>setSaveAndNext(e.target.checked)}/> حفظ وفتح الشخص التالي</label><div className="flex gap-2"><Button variant="light" disabled={candidateSaving} onClick={()=>setShowCandidateModal(false)}>إلغاء</Button><Button disabled={candidateSaving} onClick={saveCandidate}>{candidateSaving?<RefreshCw className="animate-spin" size={17}/>:<Save size={17}/>} {candidateSaving?'جارٍ الحفظ...':'حفظ'}</Button></div></div>}>
        <div onKeyDown={handleCandidateFormKeyDown}>
          <div className="mb-4 p-3 rounded-2xl bg-blue-50 text-blue-900 text-sm font-bold">النموذج التالي خاص بهذا الكشف، وترتيب الحقول مطابق لإعداد «تخصيص الحقول». اضغط Enter للحفظ من حقول الكتابة العادية.</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{enabledFields.map(renderCandidateField)}</div>
        </div>
      </Modal>

      <Modal open={showStatusModal} onClose={()=>setShowStatusModal(false)} title="إدارة حالات الأشخاص وألوانها" width="max-w-3xl">
        <div className="grid md:grid-cols-4 gap-3 mb-5"><div className="md:col-span-2"><Field label="اسم الحالة"><input value={statusForm.StatusName} onChange={e=>setStatusForm({...statusForm,StatusName:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border" placeholder="مثال: اعتذر عن السفر"/></Field></div><Field label="اللون"><input type="color" value={statusForm.ColorHex} onChange={e=>setStatusForm({...statusForm,ColorHex:e.target.value})} className="w-full h-11 rounded-xl border p-1"/></Field><div className="flex items-end"><Button onClick={saveStatus} className="w-full"><Save size={17}/> حفظ</Button></div></div>
        <div className="space-y-2">{statuses.map(s=><div key={s.StatusID} className="flex items-center justify-between border rounded-2xl p-3"><div className="flex items-center gap-3"><span className="w-4 h-4 rounded-full" style={{backgroundColor:s.ColorHex}}/><span className="font-black">{s.StatusName}</span></div><div className="flex gap-1"><button onClick={()=>setStatusForm({StatusID:s.StatusID,StatusName:s.StatusName,ColorHex:s.ColorHex,DisplayOrder:s.DisplayOrder||0})} className="p-2 rounded-lg hover:bg-slate-100"><Edit2 size={16}/></button><button onClick={()=>deleteStatus(s.StatusID)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16}/></button></div></div>)}</div>
      </Modal>

      <Modal open={showFieldsModal} onClose={()=>setShowFieldsModal(false)} title={`تخصيص حقول الكشف — ${activeSession?.SessionTitle||''}`} width="max-w-6xl">
        <div className="p-4 rounded-2xl bg-violet-50 border border-violet-100 mb-5 text-violet-900"><div className="font-black">هذه الإعدادات لهذا الكشف فقط.</div><div className="text-sm mt-1">يمكنك إظهار/إخفاء الحقول، ترتيبها، ضبط اختيارات نتيجة الاختبار، اختيار طريقة العرض (القائمة الحالية أو Radio Buttons)، وإضافة حقول اختيار متعدد وتقييم وسنة ميلاد.</div></div>
        <div className="flex justify-between gap-3 flex-wrap mb-4"><Button onClick={openAddField}><Plus size={17}/> إضافة حقل مخصص</Button><div className="text-xs text-slate-500 flex items-center">حقل الاسم أساسي، لكن يمكنك تغيير ترتيبه وظهوره في الجدول.</div></div>

        {showFieldEditor&&<div className="border-2 border-blue-100 bg-blue-50/40 rounded-3xl p-4 mb-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-lg">{fieldForm.SessionFieldID?'تعديل الحقل':'حقل مخصص جديد'}</h3>
            <button onClick={()=>setShowFieldEditor(false)} className="p-2"><X size={18}/></button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <Field label="اسم الحقل">
                <input value={fieldForm.FieldType==='birth_year'?'سنة الميلاد':fieldForm.FieldName} disabled={fieldForm.FieldType==='birth_year'} onChange={e=>setFieldForm({...fieldForm,FieldName:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border disabled:bg-slate-100"/>
              </Field>
            </div>

            <Field label="نوع الحقل">
              <select disabled={fieldForm.IsSystem} value={fieldForm.FieldType} onChange={e=>setFieldForm({...fieldForm,FieldType:e.target.value,FieldName:e.target.value==='birth_year'?'سنة الميلاد':fieldForm.FieldName,InputMode:e.target.value==='select'?fieldForm.InputMode:'default'})} className="w-full px-3 py-2.5 rounded-xl border disabled:bg-slate-100">
                <option value="text">نص</option>
                <option value="textarea">نص طويل</option>
                <option value="number">رقم</option>
                <option value="date">تاريخ</option>
                <option value="select">اختيار واحد</option>
                <option value="multiselect">اختيار متعدد - Checkboxes</option>
                <option value="checkbox">نعم / لا</option>
                <option value="score">تقييم بالدرجات</option>
                <option value="birth_year">سنة الميلاد + العمر تلقائيًا</option>
              </select>
            </Field>

            {fieldForm.FieldType==='score'&&<Field label="الدرجة القصوى"><input type="number" min="1" step="0.01" value={fieldForm.MaxScore} onChange={e=>setFieldForm({...fieldForm,MaxScore:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border"/></Field>}

            {((!fieldForm.IsSystem&&['select','multiselect'].includes(fieldForm.FieldType))||(fieldForm.IsSystem&&['TestResult','LicenseType'].includes(fieldForm.FieldKey)))&&<div className="md:col-span-2 lg:col-span-4">
              <Field label={fieldForm.FieldKey==='TestResult'?'اختيارات نتيجة الاختبار':'خيارات الحقل'} hint={fieldForm.FieldKey==='TestResult'?'هذه الاختيارات خاصة بهذا الكشف فقط، ولا علاقة لها بنتيجة اختبار القدرات في صفحة الجوازات. اكتب كل اختيار في سطر مستقل.':'اكتب كل اختيار في سطر مستقل'}>
                <textarea value={fieldForm.OptionsText} onChange={e=>setFieldForm({...fieldForm,OptionsText:e.target.value})} rows="5" className="w-full px-3 py-2.5 rounded-xl border" placeholder={'مثال:\nلائق\nغير لائق\nإعادة اختبار'}/>
              </Field>
            </div>}

            {((fieldForm.IsSystem&&['LicenseType','TestResult','PassportStatus'].includes(fieldForm.FieldKey))||(!fieldForm.IsSystem&&fieldForm.FieldType==='select'))&&<div className="md:col-span-2">
              <Field label="طريقة ظهور الاختيارات" hint="اختيار مستقل لكل حقل: يمكنك إبقاءه بالقائمة الحالية أو عرضه كـ Radio Buttons.">
                <select value={fieldForm.InputMode||'default'} onChange={e=>setFieldForm({...fieldForm,InputMode:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border">
                  <option value="default">الوضع الحالي - قائمة اختيار</option>
                  <option value="radio">Radio Buttons - أزرار اختيار</option>
                </select>
              </Field>
            </div>}

            {fieldForm.IsSystem&&fieldForm.FieldKey==='TestResult'&&<div className="md:col-span-2 lg:col-span-4 p-3 rounded-xl bg-emerald-50 text-emerald-800 text-sm font-bold">نتيجة الاختبار مستقلة الآن لكل كشف. أي تعديل هنا يؤثر على هذا الكشف فقط، ولا يغيّر «نتيجة اختبار القدرات» في الجوازات.</div>}
            {fieldForm.IsSystem&&fieldForm.FieldKey==='PassportStatus'&&<div className="md:col-span-2 lg:col-span-4 p-3 rounded-xl bg-amber-50 text-amber-800 text-sm font-bold">قيم حالة الجواز تظل «جديد / قديم شركات» للحفاظ على منطق الشركات السابقة، لكن يمكنك تغيير طريقة ظهورها بين القائمة وRadio Buttons.</div>}
            {fieldForm.FieldType==='multiselect'&&<div className="md:col-span-2 lg:col-span-4 p-3 rounded-xl bg-blue-50 text-blue-800 text-sm font-bold">هذا الحقل يسمح باختيار أكثر من قيمة في نفس الوقت، وسيظهر في نموذج الشخص كـ Checkboxes.</div>}
            {fieldForm.FieldType==='birth_year'&&<div className="md:col-span-2 lg:col-span-4 p-3 rounded-xl bg-blue-50 text-blue-800 text-sm font-bold">عند حفظ سنة الميلاد سيُظهر النظام حقل «العمر» تلقائيًا بعدها، ويحسبه من السنة المدخلة.</div>}

            <label className="flex items-center gap-2 font-bold"><input type="checkbox" checked={fieldForm.IsEnabled} disabled={fieldForm.FieldKey==='NameArabic'} onChange={e=>setFieldForm({...fieldForm,IsEnabled:e.target.checked})}/> يظهر في نموذج التسجيل</label>
            <label className="flex items-center gap-2 font-bold"><input type="checkbox" checked={fieldForm.ShowInTable} onChange={e=>setFieldForm({...fieldForm,ShowInTable:e.target.checked})}/> يظهر في الجدول</label>
            <label className="flex items-center gap-2 font-bold"><input type="checkbox" checked={fieldForm.IsRequired} disabled={fieldForm.FieldKey==='NameArabic'} onChange={e=>setFieldForm({...fieldForm,IsRequired:e.target.checked})}/> حقل مطلوب</label>
            <div className="lg:col-span-4"><Button onClick={saveField}><Save size={17}/> حفظ إعداد الحقل</Button></div>
          </div>
        </div>}

        <div className="space-y-2">{sortedFields.map((f,index)=><div key={f.SessionFieldID} className={`border rounded-2xl p-3 ${boolValue(f.IsEnabled)?'bg-white':'bg-slate-50 opacity-70'}`}><div className="flex items-center justify-between gap-3 flex-wrap"><div className="flex items-center gap-3 min-w-0"><div className="flex flex-col"><button disabled={index===0} onClick={()=>moveField(f.SessionFieldID,-1)} className="p-1 disabled:opacity-20"><ArrowUp size={15}/></button><button disabled={index===sortedFields.length-1} onClick={()=>moveField(f.SessionFieldID,1)} className="p-1 disabled:opacity-20"><ArrowDown size={15}/></button></div><div><div className="font-black flex items-center gap-2">{f.FieldName}{boolValue(f.IsSystem)&&<span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">أساسي</span>}{f.FieldType==='score'&&<span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">تقييم / {Number(f.MaxScore||10)}</span>}{f.FieldType==='birth_year'&&<span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">يحسب العمر تلقائيًا</span>}{f.FieldType==='multiselect'&&<span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700">اختيار متعدد</span>}{f.InputMode==='radio'&&<span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Radio</span>}</div><div className="text-xs text-slate-500 mt-1">{f.FieldType}{f.FieldKey==='TestResult'&&<span> • {(f.Options||[]).length} اختيار</span>}{boolValue(f.IsRequired)&&<span> • مطلوب</span>}</div></div></div><div className="flex items-center gap-2 flex-wrap"><button onClick={()=>quickUpdateField(f,{IsEnabled:!boolValue(f.IsEnabled)})} disabled={f.FieldKey==='NameArabic'} className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${boolValue(f.IsEnabled)?'bg-emerald-50 text-emerald-700':'bg-slate-100 text-slate-500'}`}>{boolValue(f.IsEnabled)?<><Eye size={13} className="inline ml-1"/>ظاهر</>:<><EyeOff size={13} className="inline ml-1"/>مخفي</>}</button><button onClick={()=>quickUpdateField(f,{ShowInTable:!boolValue(f.ShowInTable)})} className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${boolValue(f.ShowInTable)?'bg-blue-50 text-blue-700':'bg-slate-100 text-slate-500'}`}>الجدول: {boolValue(f.ShowInTable)?'نعم':'لا'}</button><button onClick={()=>openEditField(f)} className="p-2 rounded-lg hover:bg-blue-50 text-blue-600"><Edit2 size={16}/></button><button onClick={()=>deleteField(f)} disabled={f.FieldKey==='NameArabic'} className="p-2 rounded-lg hover:bg-red-50 text-red-500 disabled:opacity-20"><Trash2 size={16}/></button></div></div></div>)}</div>
      </Modal>

      <Modal open={showImportModal} onClose={()=>setShowImportModal(false)} title="استيراد بيانات الاختبارات" width="max-w-5xl">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5 p-4 bg-blue-50 rounded-2xl"><div><div className="font-black text-blue-900">النموذج مخصص للكشف الحالي</div><div className="text-xs text-blue-700 mt-1">ترتيب الأعمدة والحقول يتغير تلقائياً حسب إعدادات هذا الكشف.</div></div>{can('تحميل نموذج الاستيراد',['استيراد'])&&<Button variant="light" onClick={downloadTemplate}><Download size={17}/> تحميل نموذج Excel</Button>}</div>
        <input type="file" accept=".xlsx,.csv" onChange={e=>{setImportFile(e.target.files?.[0]||null);setImportPreview(null);}} className="w-full border rounded-2xl p-3 mb-3"/>
        <Button onClick={previewImport} disabled={!importFile||importing}>{importing?<RefreshCw className="animate-spin" size={17}/>:<Upload size={17}/>} معاينة قبل الاستيراد</Button>
        {importPreview&&<div className="mt-5"><div className="grid grid-cols-4 gap-2 mb-4"><StatCard title="الإجمالي" value={importPreview.summary?.total}/><StatCard title="جاهز" value={importPreview.summary?.valid} color="#16A34A"/><StatCard title="أخطاء" value={importPreview.summary?.invalid} color="#DC2626"/><StatCard title="تحذيرات" value={importPreview.summary?.withWarnings} color="#F59E0B"/></div><div className="max-h-[360px] overflow-auto border rounded-2xl"><table className="w-full text-sm"><thead className="sticky top-0 bg-slate-100"><tr><th className="p-2">صف</th><th className="p-2 text-right">الاسم</th><th className="p-2 text-right">الهاتف</th><th className="p-2 text-right">المندوب</th><th className="p-2 text-right">المراجعة</th></tr></thead><tbody className="divide-y">{(importPreview.rows||[]).map(r=><tr key={r.RowNumber} className={(r.errors||[]).length?'bg-red-50':(r.warnings||[]).length?'bg-amber-50':''}><td className="p-2">{r.RowNumber}</td><td className="p-2 font-bold">{r.NameArabic||'-'}</td><td className="p-2" dir="ltr">{[r.PhoneNumber,r.PhoneNumber2,r.PhoneNumber3].filter(Boolean).join(' / ')||'-'}</td><td className="p-2">{r.DelegateName||'-'}</td><td className="p-2 text-xs">{(r.errors||[]).map(x=><div className="text-red-700" key={x}>⛔ {x}</div>)}{(r.warnings||[]).map(x=><div className="text-amber-700" key={x}>⚠ {x}</div>)}{!(r.errors||[]).length&&!(r.warnings||[]).length&&<span className="text-emerald-700">جاهز</span>}</td></tr>)}</tbody></table></div><div className="mt-4 flex justify-end"><Button variant="success" disabled={!importPreview.summary?.valid||importing} onClick={confirmImport}><CheckCircle2 size={17}/> استيراد السجلات الجاهزة</Button></div></div>}
      </Modal>

      <Modal open={showColumnsModal} onClose={()=>setShowColumnsModal(false)} title={columnAction==='export'?'تخصيص أعمدة التصدير':columnAction==='image'?'تخصيص الصورة الاحترافية':columnAction==='delegateZip'?'تخصيص صور المناديب ZIP':'تخصيص أعمدة الطباعة الاحترافية'} width={['image','print','delegateZip'].includes(columnAction)?'max-w-5xl':'max-w-3xl'} footer={<div className="flex justify-end gap-2"><Button variant="light" onClick={()=>setShowColumnsModal(false)}>إلغاء</Button><Button disabled={delegateZipLoading} onClick={columnAction==='export'?doExport:columnAction==='image'?doImageExport:columnAction==='delegateZip'?exportDelegateReportsAsZip:doPrint}>{columnAction==='export'?<Download size={17}/>:columnAction==='image'||columnAction==='delegateZip'?<Image size={17}/>:<Printer size={17}/>} {delegateZipLoading&&columnAction==='delegateZip'?'جارٍ تجهيز ZIP...':'تنفيذ'}</Button></div>}>
        <div className="flex gap-2 mb-4"><button className="text-sm font-bold text-blue-600" onClick={()=>setSelectedColumns(new Set(allExportColumns.map(c=>c[0])))}>تحديد الكل</button><span className="text-slate-300">|</span><button className="text-sm font-bold text-slate-500" onClick={()=>setSelectedColumns(new Set())}>إلغاء الكل</button></div>
        {columnAction==='image'&&<div className="mb-4 p-3 rounded-2xl bg-blue-50 text-blue-900 text-sm font-bold"><Image size={16} className="inline ml-2"/>الصورة تستخدم نفس أسلوب التقرير المتقدم: عنوان احترافي، ملخص العدد، رقم الصفحة، ثيم لوني، جدول بحدود واضحة وفوتر. ويتم تصدير النتائج الظاهرة بعد البحث والفلاتر والترتيب الحالي.</div>}
        {columnAction==='delegateZip'&&<div className="mb-4 p-3 rounded-2xl bg-cyan-50 text-cyan-900 text-sm font-bold"><Image size={16} className="inline ml-2"/>سيتم إنشاء كشف صور مستقل لكل مندوب بالحقول والإعدادات المختارة ثم تجميع كل الصور داخل ملف ZIP واحد، بنفس فكرة صفحة التقارير المتقدمة.</div>}
        {columnAction==='print'&&<div className="mb-4 p-3 rounded-2xl bg-slate-50 text-slate-800 text-sm font-bold"><Printer size={16} className="inline ml-2"/>الطباعة تستخدم تنسيق التقرير الاحترافي مثل صفحة التقارير المتقدمة. الوضع الافتراضي A4 أفقي ويمكن تغييره إلى A4 طولي من الإعدادات أدناه.</div>}
        <div className="grid md:grid-cols-2 gap-2">{allExportColumns.map(([key,label])=><label key={key} className="flex items-center gap-2 border rounded-xl p-3"><input type="checkbox" checked={selectedColumns.has(key)} onChange={()=>setSelectedColumns(prev=>{const n=new Set(prev);n.has(key)?n.delete(key):n.add(key);return n;})}/><span className="font-bold">{label}</span></label>)}</div>

        {['image','print','delegateZip'].includes(columnAction)&&<div className="mt-5 border rounded-3xl p-4 bg-slate-50/70 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap"><div><h3 className="font-black text-slate-900">إعدادات شكل الصورة والطباعة</h3><p className="text-xs text-slate-500 mt-1">الهيدر والإجمالي ورقم الصفحة والفوتر والعنوان المخصص تُطبق على الصورة والطباعة معًا. عدد الصفوف والثيم اللوني خاصان بتصدير الصور.</p></div><span className="text-xs font-bold px-3 py-1 rounded-full bg-white border">إعدادات موحدة</span></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {[['showHeader','إظهار الهيدر'],['showTotal','إظهار الإجمالي'],['showPageNum','إظهار رقم الصفحة'],['showFooter','إظهار الفوتر']].map(([key,label])=><label key={key} className="flex items-center gap-2 bg-white border rounded-xl p-3 font-bold text-sm"><input type="checkbox" checked={!!imageExportSettings[key]} onChange={e=>setImageExportSettings(prev=>({...prev,[key]:e.target.checked}))}/>{label}</label>)}
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="عنوان مخصص للصورة والطباعة" hint="اتركه فارغاً لاستخدام عنوان الكشف تلقائياً"><input value={imageExportSettings.customTitle||''} onChange={e=>setImageExportSettings(prev=>({...prev,customTitle:e.target.value}))} placeholder={activeSession?.SessionTitle||'كشف الاختبارات'} className="w-full px-3 py-2.5 rounded-xl border bg-white"/></Field>
            {columnAction==='print'&&<Field label="اتجاه الطباعة" hint="الافتراضي عرضي (Landscape)"><div className="grid grid-cols-2 gap-2">{[['landscape','عرضي'],['portrait','طولي']].map(([value,label])=><button type="button" key={value} onClick={()=>setImageExportSettings(prev=>({...prev,printOrientation:value}))} className={`px-3 py-2.5 rounded-xl border font-bold transition ${imageExportSettings.printOrientation===value?'bg-blue-600 text-white border-blue-600':'bg-white text-slate-700 hover:bg-slate-50'}`}>{label}</button>)}</div></Field>}
            {columnAction!=='print'&&<Field label="أقصى عدد صفوف في الصورة"><select value={imageExportSettings.maxRowsPerImage||25} onChange={e=>setImageExportSettings(prev=>({...prev,maxRowsPerImage:Number(e.target.value)||25}))} className="w-full px-3 py-2.5 rounded-xl border bg-white">{[10,15,20,25,30,40,50].map(v=><option key={v} value={v}>{v} صف</option>)}</select></Field>}
          </div>
          {columnAction!=='print'&&<div>
            <div className="font-bold text-sm text-slate-700 mb-2">الثيم اللوني</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">{Object.entries(TEST_REPORT_COLOR_THEMES).map(([key,theme])=><button type="button" key={key} onClick={()=>setImageExportSettings(prev=>({...prev,colorTheme:key}))} className={`flex items-center gap-2 p-3 rounded-xl border-2 text-right transition ${imageExportSettings.colorTheme===key?'border-slate-900 bg-white shadow-sm':'border-transparent bg-white hover:border-slate-200'}`}><span className="w-5 h-5 rounded-full border shadow-sm" style={{background:theme.dot}}></span><span className="text-sm font-bold">{theme.label}</span></button>)}</div>
          </div>}
        </div>}

        <div className="mt-4 text-xs text-slate-500">سيتم استخدام الأشخاص المحددين إن وُجدوا، وإلا النتائج الظاهرة حالياً في الجدول. عدد النتائج في الصفحة الحالية: {displayedCandidates.length} من إجمالي {candidatePagination.total.toLocaleString('ar-EG')} نتيجة مطابقة.</div>
      </Modal>

      <Modal open={showConvertModal} onClose={()=>!converting&&setShowConvertModal(false)} title={`تسجيل ${convertIds.length>1?`${convertIds.length} أشخاص`:'الشخص'} في الجوازات`} width="max-w-6xl" footer={<div className="flex items-center justify-between gap-3 flex-wrap"><Button variant="light" disabled={converting || !can('ضبط نموذج التسجيل في الجوازات',['تسجيل في الجوازات'])} onClick={openConvertSettings}><Settings size={17}/> ضبط النموذج</Button><div className="flex justify-end gap-2"><Button variant="light" disabled={converting} onClick={()=>setShowConvertModal(false)}>إلغاء</Button><Button variant="success" disabled={converting} onClick={runConversion}>{converting?<RefreshCw className="animate-spin" size={17}/>:<UserPlus size={17}/>} تسجيل في الجوازات</Button></div></div>}>
        <div className="mb-4 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-[260px] p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm"><AlertTriangle className="inline ml-2" size={18}/>في إنشاء سجل جديد سيتم نقل البيانات المتاحة فقط. فحص رقم الهاتف يحدث هنا فقط، وليس عند الإضافة العادية من صفحة الجوازات.</div>
          <button type="button" onClick={openConvertSettings} disabled={!can('ضبط نموذج التسجيل في الجوازات',['تسجيل في الجوازات'])} className="px-4 py-3 rounded-2xl border-2 border-blue-100 bg-blue-50 text-blue-800 font-black inline-flex items-center gap-2 hover:bg-blue-100 transition"><Settings size={18}/> ضبط النموذج</button>
        </div>

        <div className="space-y-4">
          <div className="border rounded-3xl p-4 bg-slate-50/60">
            <div className="font-black text-slate-900 mb-3">بيانات السفرة والجواز</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="الشركة" required><InlineSearchSelect options={companyOptions} value={convertForm.CompanyID} onChange={v=>setConvertForm(prev=>({...prev,CompanyID:v}))} placeholder="ابحث واختر الشركة..."/></Field>
              <Field label="مدة السفر" required><select value={convertForm.TravelDuration} onChange={e=>setConvertForm(prev=>({...prev,TravelDuration:e.target.value}))} className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white"><option value="">اختر...</option>{TRAVEL_DURATIONS.map(v=><option key={v} value={v}>{v}</option>)}</select></Field>
              <Field label="حالة الجواز" required hint="نفس «حالة الجواز» في نموذج إضافة جواز جديد"><select value={convertForm.PassportStatus} onChange={e=>setConvertForm(prev=>({...prev,PassportStatus:e.target.value,PreviousCompanies:e.target.value==='قديم شركات'?prev.PreviousCompanies:[]}))} className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white"><option value="">اختر الحالة</option>{PASSPORT_STATUSES.map(v=><option key={v} value={v}>{v}</option>)}</select></Field>
              <Field label="نتيجة اختبار القدرات" required hint="نفس «نتيجة اختبار القدرات» في نموذج إضافة جواز جديد"><select value={convertForm.AbilityTest} onChange={e=>setConvertForm(prev=>({...prev,AbilityTest:e.target.value}))} className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white"><option value="">اختر نتيجة اختبار القدرات...</option>{ABILITY_TESTS.map(v=><option key={v} value={v}>{v}</option>)}</select></Field>
              {convertForm.PassportStatus==='قديم شركات'&&<div className="md:col-span-2"><Field label="الشركات السابقة" required hint="تظهر فقط عند اختيار «قديم شركات»، وبنفس البحث المتعدد المستخدم في صفحة الجوازات"><SearchableSelect options={companies.filter(c=>String(c.CompanyID)!==String(convertForm.CompanyID)).map(c=>({value:String(c.CompanyID),label:c.CompanyName,subtitle:`ID: ${c.CompanyID}`}))} multiple={true} selectedValues={(convertForm.PreviousCompanies||[]).map(String)} onMultiChange={(values)=>setConvertForm(prev=>({...prev,PreviousCompanies:values}))} placeholder="ابحث واختر الشركات السابقة..." emptyMessage="لا توجد شركات متاحة"/></Field></div>}
            </div>
          </div>

          <div className="border-2 border-blue-100 rounded-3xl p-4 bg-blue-50/35">
            <div className="font-black text-blue-950 mb-1">المهن والمندوب</div>
            <div className="text-xs text-blue-700 mb-4">القوائم هنا تظهر داخل النموذج نفسه ولا تختفي خلف النافذة؛ ويمكن البحث داخل المهن بالاسم.</div>
            <div className="grid md:grid-cols-2 gap-4 items-start">
              {passportProfessionMode==='text'
                ? <Field label="المهنة بالجواز" required><input value={convertForm.PassportProfessionText} onChange={e=>setConvertForm(prev=>({...prev,PassportProfessionText:e.target.value}))} className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white" placeholder="اكتب المهنة بالجواز..."/></Field>
                : <Field label="المهنة بالجواز" required><InlineSearchSelect options={professionOptions} value={convertForm.PassportProfessionID} onChange={v=>setConvertForm(prev=>({...prev,PassportProfessionID:v}))} placeholder="ابحث واختر المهنة بالجواز..." emptyMessage="لا توجد مهن مطابقة"/></Field>}
              <Field label="مهنة السفر" required><InlineSearchSelect options={professionOptions} value={convertForm.TravelProfessionID} onChange={v=>setConvertForm(prev=>({...prev,TravelProfessionID:v}))} placeholder="ابحث واختر مهنة السفر..." emptyMessage="لا توجد مهن مطابقة"/></Field>
              <div className="md:col-span-2"><Field label="المندوب" hint="اتركه فارغاً ليُستخدم مندوب الشخص المسجل في الاختبارات"><InlineSearchSelect options={delegateOptions} value={convertForm.DelegateID} onChange={v=>setConvertForm(prev=>({...prev,DelegateID:v}))} placeholder="بدون تغيير — أو ابحث واختر مندوباً..." emptyMessage="لا يوجد مندوب مطابق"/></Field></div>
            </div>
          </div>

          <div className="border-2 border-amber-100 rounded-3xl p-4 bg-amber-50/40">
            <label className={`flex items-start gap-3 ${can('تجاوز تكرار الهاتف')?'cursor-pointer':'cursor-not-allowed opacity-70'}`}>
              <input type="checkbox" className="mt-1" checked={!!convertForm.AllowDuplicatePhone} disabled={!can('تجاوز تكرار الهاتف')} onChange={e=>setConvertForm(prev=>({...prev,AllowDuplicatePhone:e.target.checked}))}/>
              <div>
                <div className="font-black text-amber-900">السماح بتكرار رقم الهاتف</div>
                <div className="text-sm text-amber-700 mt-1">عند تفعيل هذا الخيار سيتم إنشاء جواز جديد بصورة عادية حتى لو كان رقم الهاتف موجودًا بالفعل في جواز آخر. لا يتم ربط الشخص بالسجل القديم ولا إنشاء سفرة على نفس السجل.</div>
                {!can('تجاوز تكرار الهاتف')&&<div className="text-xs text-red-600 font-bold mt-2">تحتاج صلاحية «تجاوز تكرار الهاتف» لتفعيل هذا الخيار.</div>}
              </div>
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        open={duplicateRegisterQueue.length>0}
        onClose={()=>!markingRegistered&&setDuplicateRegisterQueue([])}
        title="رقم الهاتف مسجل بالفعل في الجوازات"
        width="max-w-2xl"
        footer={duplicateRegisterQueue.length>0 ? <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs font-bold text-slate-500">{duplicateRegisterQueue.length>1?`متبقي ${duplicateRegisterQueue.length} حالات مكررة للمراجعة`:'يمكنك تعليم الشخص كمسجل بدون إنشاء جواز مكرر.'}</div>
          <div className="flex gap-2">
            <Button variant="light" disabled={markingRegistered} onClick={()=>setDuplicateRegisterQueue(prev=>prev.slice(1))}>تخطي</Button>
            {can('تعليم كمسجل بالجوازات',['تسجيل في الجوازات'])&&<Button variant="success" disabled={markingRegistered} onClick={()=>{
              const current=duplicateRegisterQueue[0];
              if(current) markCandidateAsRegistered(current.id,current.data?.duplicate||{},true);
            }}>{markingRegistered?<RefreshCw className="animate-spin" size={17}/>:<CheckCircle2 size={17}/>} تعليم كمسجل</Button>}
          </div>
        </div> : null}
      >
        {duplicateRegisterQueue.length>0&&(()=>{
          const current=duplicateRegisterQueue[0];
          const d=current?.data?.duplicate||{};
          const candidate=candidates.find(c=>String(c.CandidateID)===String(current?.id));
          return <div className="space-y-4">
            <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900">
              <div className="font-black flex items-center gap-2"><AlertTriangle size={18}/> هذا الشخص لن يتم تكراره تلقائيًا</div>
              <div className="text-sm mt-1">رقم الهاتف موجود في جواز نشط. إذا كان هذا هو نفس الشخص، استخدم «تعليم كمسجل» لتمييزه داخل كشف الاختبارات وربطه بالسجل الموجود فقط، بدون إنشاء جواز جديد وبدون تغيير مصدر الجواز.</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="border rounded-2xl p-4 bg-slate-50"><div className="text-xs text-slate-500 font-bold">الشخص في كشف الاختبار</div><div className="font-black text-lg mt-1">{candidate?.NameArabic||'-'}</div><div className="text-sm text-slate-600 mt-1" dir="ltr">{candidate?candidatePhonesText(candidate):'-'}</div></div>
              <div className="border-2 border-emerald-100 rounded-2xl p-4 bg-emerald-50/50"><div className="text-xs text-emerald-700 font-bold">السجل الموجود في الجوازات</div><div className="font-black text-lg mt-1">{d.NameArabic||'-'}</div><div className="text-sm text-slate-600 mt-1">ملف: {d.OfficeSerial||d.PassportID||'-'} • الجواز: {d.PassportNumber||'-'}</div><div className="text-sm text-slate-600 mt-1">الشركة: {d.CompanyName||'-'}</div><div className="text-sm text-slate-600 mt-1" dir="ltr">الهاتف المطابق: {d.PhoneNumber||'-'}</div></div>
            </div>
            <div className="flex justify-end gap-2">
              {can('فتح الجواز المرتبط',['عرض الاختبارات'])&&d.PassportID&&<Button variant="light" onClick={()=>navigate(`/passports?barcodeAction=edit&barcode=${encodeURIComponent(d.OfficeSerial||d.PassportID)}`)}><ExternalLink size={17}/> فتح الجواز الموجود</Button>}
            </div>
            {!can('تعليم كمسجل بالجوازات',['تسجيل في الجوازات'])&&<div className="text-sm font-bold text-red-600 bg-red-50 border border-red-100 rounded-xl p-3">تحتاج صلاحية «تعليم كمسجل بالجوازات» لاستخدام هذا الإجراء.</div>}
          </div>;
        })()}
      </Modal>

      <Modal open={showConvertSettingsModal} onClose={()=>setShowConvertSettingsModal(false)} title="ضبط نموذج التسجيل في الجوازات" width="max-w-5xl" footer={<div className="flex justify-end gap-2"><Button variant="light" onClick={()=>setShowConvertSettingsModal(false)}>إلغاء</Button><Button onClick={saveConvertDefaultSettings}><Save size={17}/> حفظ وتطبيق</Button></div>}>
        <div className="mb-4 p-4 rounded-2xl bg-blue-50 border border-blue-100 text-blue-900 text-sm">فعّل الحقول التي تريد تثبيت قيمها. عند فتح تسجيل أي شخص في الجوازات بعد ذلك سيتم ملء هذه القيم تلقائيًا، ويمكنك تعديلها يدويًا قبل التسجيل.</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-black mb-3"><input type="checkbox" checked={!!convertSettingsDraft.enabled.CompanyID} onChange={e=>setConvertSettingEnabled('CompanyID',e.target.checked)}/> شركة افتراضية</label>
            <InlineSearchSelect disabled={!convertSettingsDraft.enabled.CompanyID} options={companyOptions} value={convertSettingsDraft.values.CompanyID} onChange={v=>setConvertSettingValue('CompanyID',v)} placeholder="اختر الشركة الافتراضية..."/>
          </div>
          <div className="border rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-black mb-3"><input type="checkbox" checked={!!convertSettingsDraft.enabled.TravelDuration} onChange={e=>setConvertSettingEnabled('TravelDuration',e.target.checked)}/> مدة سفر افتراضية</label>
            <select disabled={!convertSettingsDraft.enabled.TravelDuration} value={convertSettingsDraft.values.TravelDuration} onChange={e=>setConvertSettingValue('TravelDuration',e.target.value)} className="w-full px-3 py-2.5 rounded-xl border bg-white disabled:bg-slate-100"><option value="">اختر...</option>{TRAVEL_DURATIONS.map(v=><option key={v} value={v}>{v}</option>)}</select>
          </div>
          <div className="border rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-black mb-3"><input type="checkbox" checked={!!convertSettingsDraft.enabled.PassportStatus} onChange={e=>setConvertSettingEnabled('PassportStatus',e.target.checked)}/> حالة جواز افتراضية</label>
            <select disabled={!convertSettingsDraft.enabled.PassportStatus} value={convertSettingsDraft.values.PassportStatus} onChange={e=>setConvertSettingValue('PassportStatus',e.target.value)} className="w-full px-3 py-2.5 rounded-xl border bg-white disabled:bg-slate-100"><option value="">اختر الحالة...</option>{PASSPORT_STATUSES.map(v=><option key={v} value={v}>{v}</option>)}</select>
          </div>
          <div className="border rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-black mb-3"><input type="checkbox" checked={!!convertSettingsDraft.enabled.AbilityTest} onChange={e=>setConvertSettingEnabled('AbilityTest',e.target.checked)}/> نتيجة اختبار قدرات افتراضية</label>
            <select disabled={!convertSettingsDraft.enabled.AbilityTest} value={convertSettingsDraft.values.AbilityTest} onChange={e=>setConvertSettingValue('AbilityTest',e.target.value)} className="w-full px-3 py-2.5 rounded-xl border bg-white disabled:bg-slate-100"><option value="">اختر النتيجة...</option>{ABILITY_TESTS.map(v=><option key={v} value={v}>{v}</option>)}</select>
          </div>
          <div className="border rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-black mb-3"><input type="checkbox" checked={passportProfessionMode==='text'?!!convertSettingsDraft.enabled.PassportProfessionText:!!convertSettingsDraft.enabled.PassportProfessionID} onChange={e=>setConvertSettingEnabled(passportProfessionMode==='text'?'PassportProfessionText':'PassportProfessionID',e.target.checked)}/> مهنة الجواز الافتراضية</label>
            {passportProfessionMode==='text'
              ? <input disabled={!convertSettingsDraft.enabled.PassportProfessionText} value={convertSettingsDraft.values.PassportProfessionText} onChange={e=>setConvertSettingValue('PassportProfessionText',e.target.value)} className="w-full px-3 py-2.5 rounded-xl border bg-white disabled:bg-slate-100" placeholder="اكتب المهنة..."/>
              : <InlineSearchSelect disabled={!convertSettingsDraft.enabled.PassportProfessionID} options={professionOptions} value={convertSettingsDraft.values.PassportProfessionID} onChange={v=>setConvertSettingValue('PassportProfessionID',v)} placeholder="ابحث واختر المهنة..."/>}
          </div>
          <div className="border rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-black mb-3"><input type="checkbox" checked={!!convertSettingsDraft.enabled.TravelProfessionID} onChange={e=>setConvertSettingEnabled('TravelProfessionID',e.target.checked)}/> مهنة سفر افتراضية</label>
            <InlineSearchSelect disabled={!convertSettingsDraft.enabled.TravelProfessionID} options={professionOptions} value={convertSettingsDraft.values.TravelProfessionID} onChange={v=>setConvertSettingValue('TravelProfessionID',v)} placeholder="ابحث واختر مهنة السفر..."/>
          </div>
          <div className="md:col-span-2 border rounded-2xl p-4 bg-white">
            <label className="flex items-center gap-2 font-black mb-3"><input type="checkbox" checked={!!convertSettingsDraft.enabled.DelegateID} onChange={e=>setConvertSettingEnabled('DelegateID',e.target.checked)}/> مندوب افتراضي</label>
            <InlineSearchSelect disabled={!convertSettingsDraft.enabled.DelegateID} options={delegateOptions} value={convertSettingsDraft.values.DelegateID} onChange={v=>setConvertSettingValue('DelegateID',v)} placeholder="ابحث واختر المندوب..."/>
          </div>
        </div>
      </Modal>

      <WorkListPickerModal
        open={showWorkListPicker}
        onClose={()=>setShowWorkListPicker(false)}
        items={workListPickerItems}
        onDone={(result)=>alert(`تمت إضافة ${Number(result?.added||0).toLocaleString('ar-EG')} شخص لقائمة العمل${Number(result?.skipped||0)>0?` — ${Number(result.skipped).toLocaleString('ar-EG')} موجود بالفعل`:''}`,'success')}
      />

      <SmartRepeatQuickModal
        isOpen={showSmartRepeatModal}
        onClose={()=>setShowSmartRepeatModal(false)}
        passportData={smartRepeatPassportData}
      />

      <Modal open={showHistoryModal} onClose={()=>setShowHistoryModal(false)} title={`سجل الحالات${historyCandidate?.NameArabic?` - ${historyCandidate.NameArabic}`:''}`} width="max-w-2xl">
        <div className="space-y-3">{historyRows.length===0?<div className="text-center py-8 text-slate-500">لا يوجد سجل حالات</div>:historyRows.map(h=><div key={h.HistoryID} className="border rounded-2xl p-4"><div className="flex items-center justify-between gap-3"><div className="font-black">{h.OldStatusName||'بدون حالة'} <span className="text-slate-400 mx-2">←</span> {h.NewStatusName||'بدون حالة'}</div><div className="text-xs text-slate-500">{h.ChangedAt?new Date(h.ChangedAt).toLocaleString('ar-EG'):'-'}</div></div><div className="text-xs text-slate-500 mt-2">بواسطة: {h.ChangedByUserName||'غير محدد'}</div></div>)}</div>
      </Modal>
    </div>
  );
};

export default TestManagement;
